import actions from 'redux-form/es/actions';
import findIndex from 'lodash/findIndex';
import find from 'lodash/find';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import pullAt from 'lodash/pullAt';
import moment from 'moment';
import axios from 'axios';
import queryStringParser from 'utils/queryStringParser';
import store from 'router/store';
import * as actionTypes from 'actions/actionTypes';
import { getLanguage } from 'actions/localization';
import {
  getRegisterData,
  getUserSession,
  logout,
  redirectToSS,
  requestOTPCzChangeProduct,
  putAdditionalConsents,
} from 'actions/authentication';
import { putMarketingConsents } from 'actions/marketingConsents';
import { mwResumeFlow } from 'actions/mobileWalletApp';
import putLog from 'actions/putLog';
import initialState from 'config/initialLoanState';
import COUNTRIES from 'consts/countries';
import {
  AUTH_METHOD_BANK,
  AUTH_METHOD_ID_CARD,
  AUTH_METHOD_IMPERSONATION,
  AUTH_METHOD_MOBILE_ID,
} from 'consts/loan/authMethodTypes';
import { CHANNEL_JOURNEY_APPLY_N, CHANNEL_LOAN_APP, CHANNEL_MOBILE_WALLET } from 'consts/loan/channels';
import {
  STEP_PRODUCT,
  STEP_PSYCHOMETRIC,
  STEP_REIDENTIFY_CUSTOMER,
  STEP_SYSTEM_AUTHORITY,
  STEP_ALMOST_THERE,
  STEP_INFORMATION,
  STEP_SUBMIT,
  STEP_SACC_LOADING,
} from 'consts/loan/steps';
import {
  financialDataValidationErrors,
  loansArray,
  possiblePeriodsCL,
  PROVISO_MOCK_URL,
  workingStateTypes,
  MEDICARE_CARD_COLOR_GREEN,
  DRIVING_LICENSE, MEDICARE, PASSPORT,
} from 'consts';
import {
  constructErrorMessage,
  creditApplicationInitPath,
  fromCents,
  getErrorCode,
  getIndexStepByName,
  getRootURL,
  isNullOrEmpty,
  normalizeMsisdn,
  removeNonDigitCharacters,
  toCents,
} from 'utils/helpers';
import { getAnalyticsObject, isInstallment } from 'utils/gaAnalytics';
import { affiliateRequestContractSubmitted, affiliateRequestCustomerRejected } from 'tracking/affiliate';
import {
  getPersistentItem,
  getSavedAppState,
  getSignUpGateData,
  removeCachedQueryString,
  savePersistentItem,
  savePrincipalValue,
} from 'utils/localRepository';
import { createTMXScript, invokeThreadMetrixProfiling } from 'utils/tmx';
import getCookiesManager from 'utils/cookiesManager';
import { TAX_AUTHORITY_CONSENTS_CLICKED, TAX_AUTHORITY_CONSENTS_URL } from 'consts/loan/localStorage';
import { CREDIT_REJECTION_BDC_SCORING_NEGATIVE, CREDIT_REJECTION_EXT_SCORE_CUT } from 'consts/loan/responseStatusCodes';
import { track } from './analytics';
import { getStepAfterProvidentScoring } from '../utils/scoring';
import getAnalyticsObjectAU from '../utils/analytics/getAnalyticsObjectAU';
import sessionRepository from '../utils/sessionRepository';
import { CENTERLINK_STATUS } from '../consts/loan/centerlinkStatuses';
import { REGISTRATION_CONSENTS_PAYLOAD, SCORING_TIMEOUT } from '../consts/loan/AU';

const { change, stopSubmit } = actions;

export function goToNextPage() {
  return {
    type: actionTypes.STEP_FORWARD,
  };
}

export const goToSpecificStep = (stepType) => ({
  type: actionTypes.GO_TO_SPECIFIC_STEP,
  data: { stepType },
});

export function getProvisoUrl() {
  return {
    types: [
      actionTypes.GET_PROVISO_IFRAME_URL,
      actionTypes.GET_PROVISO_IFRAME_URL_SUCCESS,
      actionTypes.GET_PROVISO_IFRAME_URL_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application-au/initialise-proviso?redirect_url=${window.location.origin + window.location.pathname}`,
      { hideError: true }
    ),
    afterSuccess: (dispatch, getState, response) => {
      if (response.data.iframeUrl === PROVISO_MOCK_URL) {
        dispatch(goToNextPage());
      }
    },
  };
}

export function setCustomerEmail(email) {
  return {
    type: actionTypes.SET_CUSTOMER_EMAIL,
    data: email,
  };
}

export function setLegacyBalance(legacyBalance) {
  return {
    type: actionTypes.SET_LEGACY_BALANCE,
    data: legacyBalance,
  };
}

export function setBrokerApp() {
  return {
    type: actionTypes.SET_BROKER_APP,
  };
}

export function setBrokerUrl(brokerUrl) {
  return {
    type: actionTypes.SET_BROKER_URL,
    data: brokerUrl,
  };
}

const setFieldsValues = (dispatch, values) => {
  Object.keys(values).forEach((field) => {
    if (field === 'emailPending' && values.emailPending !== null) {
      dispatch(change('loan', 'email', values.emailPending));
    } else {
      dispatch(change('loan', field, values[field]));
    }
  });
};

const getMxPrefilledAddress = (address) => {
  let street = '';
  let externalNumber = '';
  if (address.street) {
    street = address.street.split(', ');
    if (street.length > 1) {
      externalNumber = street.pop();
    }
    street = street.join(', ');
  }

  return {
    province: address.province || '',
    city: address.city || '',
    municipality: address.municipality || '',
    neighbourhood: address.neighbourhood || '',
    street,
    externalNumber,
    internalNumber: address.door || '',
    postalCode: address.postcode || '',
  };
};

const getLoanFormValues = (loanFormValues, authentication) => {
  if (COUNTRY === 'CZ') {
    const {
      address: { city, street, postcode },
      citizenships,
      countryOfBirth,
      cityOfBirth,
      personalDocumentNumber,
    } = authentication;
    const streetComponents = street && street.split(' ');
    const nextLoanFormValues = {
      ...loanFormValues,
      citizenship: citizenships[0],
      locality: cityOfBirth,
      placeOfBirth: cityOfBirth,
      country: countryOfBirth,
      countryOfBirth,
      documentNumber: personalDocumentNumber,
      city,
      postcode,
    };
    if (streetComponents) {
      nextLoanFormValues.street = streetComponents[0];
      nextLoanFormValues.house = streetComponents[1];
      nextLoanFormValues.addressInput = `${street}, ${city}, ${postcode}`;
    }
    return nextLoanFormValues;
  }

  if (COUNTRY === 'LT') {
    const {
      msisdn,
    } = authentication;
    const nextLoanFormValues = {
      ...loanFormValues,
      msisdn,
    };

    return nextLoanFormValues;
  }

  return loanFormValues;
};

const restoreLoanFormValues = (dispatch, formValues = {}) => {
  const restrictedFields = ['otp'];
  if (COUNTRY === 'LT') {
    restrictedFields.push('agreeToVerification', 'agreeToElectronicServices');
  }
  if (COUNTRY === 'ES') {
    restrictedFields.push('submitFinancialdataSign', 'submitAgree');
  }
  const values = { ...formValues };
  restrictedFields.forEach((field) => delete values[field]);
  setFieldsValues(dispatch, values);
};

const getBrokerAppPrefilledValues = (formInitialValues, authentication, loanData) => {
  let prefilledValues = formInitialValues;
  const { address2, metadata } = authentication;
  const { financialData } = loanData;
  if (address2) {
    prefilledValues = {
      ...prefilledValues,
      ...getMxPrefilledAddress(address2),
    };
  }
  prefilledValues = {
    ...prefilledValues,

    // Customer info step
    firstName: authentication.firstName || '',
    secondFirstName: authentication.secondFirstName || '',
    lastName: authentication.lastName || '',
    secondLastName: authentication.secondLastName || '',

    // Customer contacts step
    email: authentication.email || authentication.emailPending || '',

    // Customer address step
    residentialStatus: financialData.residence || '',

    // Family step
    maritalStatus: financialData.maritalStatus || '',

    // Education and work
    education: metadata.education || '',
    occupation: metadata.occupation || '',
    company: metadata.company || '',
    employmentDuration: metadata.employmentDuration || '',

    // Income
    netIncome: parseInt(metadata.netIncome, 10) >= 0 ? fromCents(metadata.netIncome).toString() : '',

    // Msisdn

  };

  if (COUNTRY === 'ES') {
    let street = '';
    let streetNumber = '';
    if (authentication.address.street) {
      street = authentication.address.street.split(' ');
      if (street.length > 1) {
        streetNumber = street.pop().split('-')[0];
      }
      street = street.join(' ');
    }

    let dob = authentication.dateOfBirth;
    let birthDay = '';
    let birthMonth = '';
    let birthYear = '';
    if (dob) {
      dob = dob.split('-');
      birthYear = dob[0];
      birthMonth = dob[1];
      birthDay = dob[2];
    }

    prefilledValues = {
      ...prefilledValues,
      street,
      streetNumber,
      birthYear,
      birthMonth,
      birthDay,
      city: authentication.address.city || '',
      door: authentication.address.door || '',
      floor: authentication.address.floor || '',
      postcode: authentication.address.postcode || '',
      province: authentication.address.province || '',
      msisdn2: authentication.address.msisdn2 || '',
      maritalStatus: metadata.maritalStatus || '',
      householdChildren: metadata.householdChildren || '',
    };
  }

  return prefilledValues;
};

const getAUPrefilledValues = (authentication, loan) => {
  const identDocumentTypes = {
    PASSPORT,
    MEDICARE_CARD: MEDICARE,
    DRIVER_LICENCE: DRIVING_LICENSE,
  };
  const { customer: { address }, financialData } = loan;
  const { metadata } = authentication;
  const identDocumentType = identDocumentTypes[authentication.identifierType];
  let medicareExpirationDay;
  let medicareExpirationMonth;
  let medicareExpirationYear;
  let drivingLicenceExpirationDay;
  let drivingLicenceExpirationMonth;
  let drivingLicenceExpirationYear;
  let passportExpirationDay;
  let passportExpirationMonth;
  let passportExpirationYear;
  let soleEarnerIndicator;
  let secondJob;
  let loanPayments;
  let creditCards;

  if (metadata.MEDICARE_EXPIRY_DATE) {
    const medicareExpiryDate = moment(metadata.MEDICARE_EXPIRY_DATE);
    medicareExpirationDay = medicareExpiryDate.date().toString();
    medicareExpirationMonth = (medicareExpiryDate.month() + 1).toString();
    medicareExpirationYear = medicareExpiryDate.year().toString();
  }

  if (metadata.DRIVING_LICENCE_EXPIRY_DATE) {
    const drivingLicenceExpiryDate = moment(metadata.DRIVING_LICENCE_EXPIRY_DATE);
    drivingLicenceExpirationDay = drivingLicenceExpiryDate.date().toString();
    drivingLicenceExpirationMonth = (drivingLicenceExpiryDate.month() + 1).toString();
    drivingLicenceExpirationYear = drivingLicenceExpiryDate.year().toString();
  }

  if (metadata.PASSPORT_EXPIRY_DATE) {
    const passportExpiryDate = moment(metadata.PASSPORT_EXPIRY_DATE);
    passportExpirationDay = passportExpiryDate.date().toString();
    passportExpirationMonth = (passportExpiryDate.month() + 1).toString();
    passportExpirationYear = passportExpiryDate.year().toString();
  }

  if (financialData.soleEarnerIndicator) {
    soleEarnerIndicator = financialData.soleEarnerIndicator === 'true' ? 'yes' : 'no';
  }

  if (financialData.loanPaymentsCount) {
    loanPayments = parseInt(financialData.loanPaymentsCount, 10) > 0 ? 'yes' : 'no';
  }
  if (INCREASE_APP) {
    if (
      !('loanPaymentsCount' in financialData)
      && !('loanPaymentsFrequency' in financialData)
      && !('loanPaymentsTotalRepayments' in financialData)
    ) {
      loanPayments = 'no';
    }
  }

  if (financialData.creditCardsCount) {
    creditCards = parseInt(financialData.creditCardsCount, 10) > 0 ? 'yes' : 'no';
  }
  if (INCREASE_APP) {
    if (
      !('creditCardsCount' in financialData)
      && !('creditCardsTotalLimit' in financialData)
    ) {
      creditCards = 'no';
    }
  }

  if (financialData.employmentType === 'FULL_TIME' || financialData.employmentType === 'PART_TIME') {
    secondJob = financialData.secondEmployerName ? 'yes' : 'no';
  }

  return {
    ...authentication,
    ...loan,
    ...financialData,
    ...address,
    addressState: address.state,
    identDocumentType,
    passportNumber: identDocumentType === PASSPORT ? authentication.identifier : null,
    medicareNumber: identDocumentType === MEDICARE ? authentication.identifier : null,
    medicareReferenceNumber: metadata.MEDICARE_REFERENCE_NUMBER,
    cardColour: metadata.CARD_COLOUR,
    medicareExpirationDay,
    medicareExpirationMonth,
    medicareExpirationYear,
    drivingLicenceNumber: identDocumentType === DRIVING_LICENSE ? authentication.identifier : null,
    drivingLicenceState: metadata.DRIVING_LICENCE_STATE,
    drivingLicenceExpirationDay,
    drivingLicenceExpirationMonth,
    drivingLicenceExpirationYear,
    countryOfOrigin: metadata.COUNTRY_OF_ORIGIN,
    countryOfIssue: metadata.PASSPORT_COUNTRY_OF_ISSUE,
    passportExpirationDay,
    passportExpirationMonth,
    passportExpirationYear,
    soleEarnerIndicator,
    education: financialData.educationLevel,
    occupation: financialData.employmentType,
    secondJob,
    secondCompany: financialData.secondEmployerName,
    incomeAmount: fromCents(financialData.salary),
    incomePayDayDate: financialData.incomeNextPayDay,
    housingCosts: fromCents(financialData.housingCosts),
    totalLivingExpenses: fromCents(financialData.totalMonthlyLivingExpenses),
    foodGroceriesExpenses: fromCents(financialData.foodGroceries),
    transportExpenses: fromCents(financialData.transport),
    healthMedicalExpenses: fromCents(financialData.healthMedical),
    insuranceExpenses: fromCents(financialData.insurance),
    utilitiesExpenses: fromCents(financialData.utilities),
    phoneInternetExpenses: fromCents(financialData.phoneInternet),
    otherExpenses: fromCents(financialData.other),
    loanPayments,
    totalRepayments: fromCents(financialData.loanPaymentsTotalRepayments),
    frequency: financialData.loanPaymentsFrequency,
    creditCards,
    totalLimit: fromCents(financialData.creditCardsTotalLimit),
  };
};

const getMXPrefilledValues = (authentication, loan) => {
  const { address2 } = authentication;
  const { financialData } = loan;
  let residentialStatus;
  let address;
  let signUpGateValues;

  const signUpGateData = getSignUpGateData();

  if (signUpGateData) {
    signUpGateValues = {
      email: signUpGateData.sugEmail,
      firstName: signUpGateData.sugFirstName,
      secondFirstName: signUpGateData.sugSecondName,
      lastName: signUpGateData.sugPaternalName,
      secondLastName: signUpGateData.sugMaternalName,
    };
  }

  if (financialData.residence) {
    residentialStatus = financialData.residence;
  }

  if (address2) {
    address = getMxPrefilledAddress(address2);
  }

  return {
    ...authentication,
    ...loan,
    ...financialData,
    ...address,
    ...signUpGateValues,
    residentialStatus,
    firstDrawAmount: fromCents(loan.firstDrawAmount),
    netIncome: parseInt(financialData.netIncome, 10) >= 0 ? fromCents(financialData.netIncome).toString() : '',
  };
};

export function saveLoanForm() {
  return {
    type: actionTypes.SAVE_LOAN_STATE,
  };
}

function dataToPutCustomerInfoEs(data) {
  return {
    address: {
      city: data.city,
      door: data.door,
      floor: data.floor,
      postcode: data.postcode,
      province: data.province,
      street: `${data.street} ${data.streetNumber}`,
    },
    customer: {
      countryOfBirth: data.countryOfBirth,
      dateOfBirth: [
        parseInt(data.birthYear, 10),
        parseInt(data.birthMonth, 10),
        parseInt(data.birthDay, 10),
      ],
      firstName: data.firstName,
      gender: data.gender,
      lastName: data.lastName,
      msisdn2: data.msisdn2,
      secondLastName: data.secondLastName,
    },
    email: { email: data.email },
    password: { password: data.password },
  };
}

function dataToPutCustomerInfoMx(data, loan, step) {
  const { customer } = loan;
  const payload = {
    customer: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      secondFirstName: customer.secondFirstName,
      secondLastName: customer.secondLastName,
    },
  };
  if (step === 'CUSTOMER_MX_CONTACTS') {
    payload.email = data.email;
    payload.password = data.password;
  } else if (data.postalCode) {
    payload.address = {
      city: data.city,
      door: data.internalNumber,
      municipality: data.municipality,
      neighbourhood: data.neighbourhood,
      postcode: data.postalCode,
      province: data.province,
      street: `${data.street}, ${data.externalNumber}`,
      residence: data.residentialStatus,
    };
  }

  return payload;
}

async function promiseForPutInfoCrediteaAction(client, data, loan, step, isMXIncomePage = false) {
  let info;
  let route;
  switch (COUNTRY) {
    case 'ES':
      info = dataToPutCustomerInfoEs(data);
      route = 'customer/info-es';
      break;
    case 'MX':
      info = dataToPutCustomerInfoMx(data, loan, step);
      route = 'customer-mx/info-mx';
      break;
    default:
      info = {};
      route = '';
  }

  // eslint-disable-next-line no-return-await -- node upgrade
  return await client.put(
    `${API_HOST}/${route}`,
    info,
    { hideError: true, preventLogout: true, isMXIncomePage }
  );
}

export const putScreenName = (screenName, ocm = false) => ({
  types: [
    actionTypes.PUT_SCREEN_NAME,
    actionTypes.PUT_SCREEN_NAME_SUCCESS,
    actionTypes.PUT_SCREEN_NAME_FAIL,
  ],
  promise: (client) => (
    client.put(
      ocm ? `${API_HOST}/credit-application/screen-name` : `${API_HOST}/${creditApplicationInitPath()}/screen-name`,
      { screenName },
      { hideError: true }
    )
  ),
});

// FOR SAVING SCREEN_NAME AFTER SCORING
export const putScreenNameAU = (stepName) => ({
  types: [
    actionTypes.PUT_SCREEN_NAME,
    actionTypes.PUT_SCREEN_NAME_SUCCESS,
    actionTypes.PUT_SCREEN_NAME_FAIL,
  ],
  promise: (client) => (
    client.put(
      `${API_HOST}/credit-application-au/step-name`,
      { stepName },
      { hideError: true }
    )
  ),
});

export function putInfoCrediteaAction(data, loan, step, isMXIncomePage = false) {
  return {
    types: [
      actionTypes.PUT_INFO_CREDITEA,
      actionTypes.PUT_INFO_CREDITEA_SUCCESS,
      actionTypes.PUT_INFO_CREDITEA_FAIL,
    ],
    data,
    promise: (client) => new Promise(async (resolve, reject) => {
      try {
        await promiseForPutInfoCrediteaAction(client, data, loan, step, isMXIncomePage);
        resolve();
      } catch (error) {
        reject();
      }
    }),
    afterSuccess: (dispatch, getState) => {
      if (COUNTRY === 'ES') {
        dispatch(putMarketingConsents(getState(), data));
      }
      if (step === 'CUSTOMER_MX_CONTACTS') {
        dispatch(putMarketingConsents(getState(), data, () => (dispatch(putScreenName(step)))));
      }
      if (COUNTRY === 'MX' && step !== 'CUSTOMER_MX_CONTACTS' && step !== 'INCOME_MX') {
        dispatch(putScreenName(step));
      }
    },
    afterFailure: (dispatch, getState, response) => {
      affiliateRequestCustomerRejected(dispatch, getState);
      if (COUNTRY === 'ES') {
        if (step === 'CUSTOMER_ES_CONTACTS' && response.data.errCode === 1267) {
          dispatch(
            change('loan', 'emailInUse', data.email)
          );
        }
      }
    },
  };
}

export function setSelectedProduct(product) {
  return {
    type: actionTypes.SET_SELECTED_PRODUCT,
    payload: {
      product,
    },
  };
}

function educationWorkDetailsCrediteaData(data) {
  const educationWorkDetails = {
    occupation: data.occupation,
  };
  const itemIndex = findIndex(workingStateTypes, (item) =>
    item.type === data.occupation);
  const workingStateType = workingStateTypes[itemIndex] || {};
  if (workingStateType.isCompanyNeeded) {
    educationWorkDetails.company = data.company;
  }
  if (workingStateType.isEmploymentDurationNeeded) {
    educationWorkDetails.employmentDuration = data.employmentDuration;
  }
  if (workingStateType.isActivityTypeNeeded) {
    educationWorkDetails.activityType = data.activityType;
  }
  if (workingStateType.isIndustryNeeded) {
    educationWorkDetails.industry = data.industry;
  }
  if (workingStateType.isRetirementTypeNeeded) {
    educationWorkDetails.retirementPensionedBy = data.retirementPensionedBy;
  }
  if (workingStateType.isUniversityNeeded) {
    educationWorkDetails.studiesAtInstitution = data.studiesAtInstitution;
    if (data.studiesAtInstitution === 'OTHER') {
      educationWorkDetails.studiesAtOtherInstitution = data.studiesAtOtherInstitution;
    }
  }
  if (workingStateType.isFreelancersIndustryNeeded) {
    educationWorkDetails.freelancersIndustry = data.freelancersIndustry;
  }
  if (COUNTRY === 'PL') {
    if (workingStateType.isEmploymentTerminationNeeded) {
      const dateString = `${data.employmentTerminationYear}-${data.employmentTerminationMonth}-${data.employmentTerminationDay}`;
      educationWorkDetails.employmentProvisionTermination = moment(dateString, 'YYYY-M-D').format('YYYY-MM-DD');
    }
  } else {
    educationWorkDetails.education = data.education;
  }

  return educationWorkDetails;
}

export function rejectLoan(rejectReason) {
  return {
    types: [
      actionTypes.PUT_REJECT_LOAN,
      actionTypes.PUT_REJECT_LOAN_SUCCESS,
      actionTypes.PUT_REJECT_LOAN_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application-es/reject`,
      { rejectReason }
    ),
  };
}

export function resetBankScrapingResult() {
  return {
    type: actionTypes.RESET_BANK_SCRAPING_RESULT,
  };
}

export function initApplicationScoringSuccess(success) {
  return {
    types: [
      actionTypes.INIT_APPLICATION_SCORING,
      actionTypes.INIT_APPLICATION_SCORING_SUCCESS,
      actionTypes.INIT_APPLICATION_SCORING_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/application-scoring-au/init`,
      {
        bankScrapingInit: success ? 'SUCCESS' : 'FAILURE',
      }
    ),
    afterSuccess: (dispatch) => {
      dispatch(resetBankScrapingResult());
    },
  };
}

export const setRescoringToDoForOpenCreditApplication = (value) => ({
  type: actionTypes.SET_RESCORING_TO_DO_FOR_OPEN_CREDIT_APPLICATION,
  data: value,
});

export const setSteps = (steps) => ({
  type: actionTypes.SET_STEPS,
  data: steps,
});

export const setLastStatusPage = (page) => ({
  type: actionTypes.SET_LAST_STATUS_PAGE,
  data: page,
});

const initTmxSession = (data) => {
  let URL;
  if (COUNTRY === 'CZ') {
    URL = `${API_HOST}/tmx/session`;
  } else {
    URL = `${API_HOST}/tmx/init-session`;
  }
  return {
    types: [
      actionTypes.TMX_INIT,
      actionTypes.TMX_INIT_SUCCESS,
      actionTypes.TMX_INIT_FAIL,
    ],
    promise: (client) => client.post(URL, data, { hideError: true, preventLogout: true }),
    afterSuccess: (dispatch, getState, response) => {
      const responseData = response.data;
      const state = getState();
      if (COUNTRY === 'ES' && state.loan.libertoUnsucceed) {
        dispatch(goToNextPage());
      }
      if (COUNTRY === 'AU' && state.loan.bankScrapingResult.status === 'COMPLETE') {
        dispatch(initApplicationScoringSuccess(true));
      }
      if (isObject(responseData) && responseData.should_init === true) {
        let tmxParams;
        if (COUNTRY === 'CZ') {
          tmxParams = {
            org_id: responseData.orgId,
            session_id: responseData.sessionId,
          };
        } else {
          tmxParams = responseData.PARAMS;
        }
        if (isObject(tmxParams) && !isEmpty(tmxParams)) {
          if (COUNTRY === 'AU') {
            createTMXScript(tmxParams);
          } else {
            invokeThreadMetrixProfiling(tmxParams);
          }
        }
      }
    },
  };
};

const dispatchInitTmxSessionEvent = (dispatch, loan) => {
  let tmxData;
  switch (COUNTRY) {
    case 'PL':
    case 'ES':
    case 'AU':
    case 'MX':
      tmxData = {
        selected_amount: loan.product.principal,
        top_domain: 'hapicredito.com',
      };
      break;
    case 'FI':
    case 'LV':
    case 'LT':
    case 'EE':
    case 'CZ':
      tmxData = {
        selected_amount: loan.product.principal,
        top_domain: window.location.hostname,
      };
      break;
    default:
      tmxData = null;
  }
  if (tmxData) {
    dispatch(initTmxSession(tmxData));
  }
};

const resetIsFilledByBroker = (getState) => {
  const { authentication } = getState();
  if (authentication.authenticationMethod === 'IMPERSONATION') {
    savePersistentItem(authentication.identifier, { isFilledByBroker: false });
  }
};

// const getSalaryData = () => {
//   const config = { hideError: true };
//   return {
//     types: [
//       actionTypes.GET_SALARY_DATA,
//       actionTypes.GET_SALARY_DATA_SUCCESS,
//       actionTypes.GET_SALARY_DATA_FAIL,
//     ],
//     promise: (client) => client.get(`${API_HOST}/customer/salaryData`, config),
//     afterSuccess: (dispatch, getState, response) => {
//       const income = response.data.income;
//       if (income > 0) {
//         const incomeValue = {
//           netIncome: Math.round(income),
//         };
//         setFieldsValues(dispatch, incomeValue);
//       }
//     },
//   };
// };

// const fetchExternalRegistries = () => {
//   const config = { hideError: true };
//   return {
//     types: [
//       actionTypes.FETCH_EXTERNAL_REGISTRIES,
//       actionTypes.FETCH_EXTERNAL_REGISTRIES_SUCCESS,
//       actionTypes.FETCH_EXTERNAL_REGISTRIES_FAIL,
//     ],
//     // promise: (client) => client.get(`${API_HOST}/${creditApplicationInitPath()}`, config),
//     // TODO: temporary solution
//     promise: (client) => client.post(
//       `${API_HOST}/credit-application-lt/fetch-external-registries`,
//       {},
//       config,
//     ).then(
//       () => client.get(`${API_HOST}/${creditApplicationInitPath()}`, config)
//     ),
//     afterSuccess: (dispatch) => {
//       dispatch(getSalaryData());
//     },
//     afterFailure: (dispatch) => {
//       dispatch(saveLoanForm());
//     },
//   };
// };

export const checkExternalPolicies = () => {
  const config = { hideError: true };
  return {
    types: [
      actionTypes.CHECK_EXTERNAL_POLICIES,
      actionTypes.CHECK_EXTERNAL_POLICIES_SUCCESS,
      actionTypes.CHECK_EXTERNAL_POLICIES_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application-lt/check-external-policies`,
      {},
      config
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { income } = response.data;
      if (income > 0) {
        const incomeValue = {
          netIncome: Math.round(income),
        };
        setFieldsValues(dispatch, incomeValue);
      }
      dispatch(putScreenName('REGISTRIES_LT'));

      if (response.data.status === 'REJECTED') {
        dispatch(track('rejected', {
          analyticsLT: () => {
            const {
              authentication: { id: authenticationId, isOldCustomer },
              loan: { id: loanId },
            } = getState();
            return {
              page: 'checkRegistries',
              userID: authenticationId,
              applicationID: loanId,
              offerSelected: parseFloat(sessionStorage.getItem('offerSelected')),
              clientType: isOldCustomer ? 'Returning Client' : 'New Client',
            };
          },
        }));
      }
    },
  };
};

export function closeGuarantorApplication(creditApplicationId) {
  const url = `${API_HOST}/credit-application-es/${creditApplicationId}/close-guarantor-application`;
  return {
    types: [
      actionTypes.CLOSE_GUARANTOR_APPLICATION,
      actionTypes.CLOSE_GUARANTOR_APPLICATION_SUCCESS,
      actionTypes.CLOSE_GUARANTOR_APPLICATION_FAIL,
    ],
    promise: (client) => client.put(url, { hideError: true }),
  };
}

export const getApplicationScoringStatus = (scoringId, isPresonificationWithGuarantor = false) => {
  if (COUNTRY === 'ES') {
    return {
      types: [
        actionTypes.SCORING_STATUS_ES,
        actionTypes.SCORING_STATUS_ES_SUCCESS,
        actionTypes.SCORING_STATUS_ES_FAIL,
      ],
      promise: (client) => client.get(
        `${API_HOST}/credit-application-es/scoring/${scoringId}`,
        { hideError: true, timeout: initialState.statusMaxTimeout }
        // eslint-disable-next-line no-confusing-arrow
      ),
      afterSuccess: (dispatch, getState, response) => {
        const { loan } = getState();
        const isFinished = response.data.currentStep === 'FINISHED';
        const isRejected = response.data.currentStep === 'REJECTED';
        const isWaitingForCustomer = response.data.currentStep === 'WAITING_FOR_CUSTOMER'
          || response.data.currentStep === 'WAITING_FOR_CUSTOMER_AND_NORDIGEN_BEFORE_OFFER';
        if (loan.rescoringToDoForOpenCreditApplication && (isFinished || isWaitingForCustomer)) {
          dispatch(setRescoringToDoForOpenCreditApplication(
            loan.rescoringOpenCreditApplication - 1
          ));
        } else if (loan.rescoringToDoForOpenCreditApplication && isRejected) {
          dispatch(setRescoringToDoForOpenCreditApplication(
            0
          ));
        }
        if (loan.isWaitingForOffer && loan.finalState !== true) {
          window.setTimeout(
            () => {
              // eslint-disable-next-line max-len
              dispatch(getApplicationScoringStatus(scoringId, isPresonificationWithGuarantor));
            },
            loan.waitingForOfferInterval
          );
        }
        if (isRejected) {
          const page = loan.lastStatusPage || 'status_1';
          dispatch(track('rejected', {
            analyticsES: (state) => {
              const {
                authentication: { id: authenticationId, isOldCustomer },
                loan: { id: loanId, financialData },
              } = state;
              return {
                page,
                userID: authenticationId,
                applicationID: loanId,
                offerSelected: fromCents(financialData.appliedForAmount),
                clientType: isOldCustomer ? 'Returning Client' : 'New Client',
              };
            },
          }));
        }
        if (isRejected && response.result.state === 410) {
          // eslint-disable-next-line no-use-before-define
          dispatch(submitLoanStep('LOAN_QUESTIONS', loan.financialData, loan));
        }

        if (isPresonificationWithGuarantor && isFinished) {
          dispatch(closeGuarantorApplication(loan.id));
        }
      },
    };
  }
  if (COUNTRY === 'AU') {
    const URL = `${API_HOST}/application-scoring-au/status`;
    return {
      types: [
        actionTypes.SCORING_STATUS_AU,
        actionTypes.SCORING_STATUS_AU_SUCCESS,
        actionTypes.SCORING_STATUS_AU_FAIL,
      ],
      promise: (client) => client.get(URL, { hideError: true, timeout: SCORING_TIMEOUT }),
      afterSuccess: (dispatch, getState, response) => {
        const { loan } = getState();

        if (loan.channel === 'OCM-WEB' && response.data.applicationState === 'DECISION_ENGINE_REJECTION') {
          const cookiesManager = getCookiesManager();
          cookiesManager.saveValue(true, 'ocmRejection', 'AU');

          dispatch(logout());
        }

        if (loan.isWaitingForOffer && response.data.finalState !== true) {
          /*
          Until finalState is not true, changes to applicationState
          are expected. Once finalState is true, applicationState value
          is final and will not change
          */
          window.setTimeout(() => {
            dispatch(getApplicationScoringStatus());
          }, loan.waitingForOfferInterval);
        }
      },
    };
  }
  const data = { action: 'getApplicationScoringStatus' };
  if (DEBUG) {
    data.reason = `action is not supported in ${COUNTRY} app`;
  }
  return {
    data,
    type: actionTypes.ACTION_SKIPPED,
  };
};

export const setLoanFormFooterVisibility = (visible) => ({
  type: actionTypes.SET_LOAN_FORM_FOOTER_VISIBILITY,
  data: visible,
});

export function verificationStateRestore(persistentItem) {
  return {
    type: actionTypes.VERIFICATION_STATE_RESTORE,
    data: persistentItem,
  };
}

export function restoreLoanState(data) {
  return {
    type: actionTypes.RESTORE,
    data,
  };
}

function setCreditApplicationId(creditApplicationId) {
  return {
    type: actionTypes.SET_CREDIT_APPLICATION_ID,
    data: creditApplicationId,
  };
}

function getExtraServices(types) {
  const URL = `${API_HOST}/credit-application/extra-services`;
  return {
    types,
    promise: (client) => client.get(URL),
  };
}

export function getRepaymentPlanAvailability() {
  const types = [
    actionTypes.REPAYMENT_PLAN_AVAILABILITY,
    actionTypes.REPAYMENT_PLAN_AVAILABILITY_SUCCESS,
    actionTypes.REPAYMENT_PLAN_AVAILABILITY_FAIL,
  ];
  return getExtraServices(types);
}

export function removeActionError() {
  return {
    type: actionTypes.REMOVE_ACTION_ERROR,
  };
}

export function getCalmaAvailability() {
  const types = [
    actionTypes.CALMA_AVAILABILITY,
    actionTypes.CALMA_AVAILABILITY_SUCCESS,
    actionTypes.CALMA_AVAILABILITY_FAIL,
  ];
  return getExtraServices(types);
}

export function getExtraServiceQuestions() {
  return {
    types: [
      actionTypes.EXTRA_SERVICE_QUESTIONS,
      actionTypes.EXTRA_SERVICE_QUESTIONS_SUCCESS,
      actionTypes.EXTRA_SERVICE_QUESTIONS_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application/extra-service-questions`, { hideError: true }),
  };
}

function getExtraServicesAdditionalInfoPL(services) {
  const types = [
    actionTypes.EXTRA_SERVICES_ADDITONAL_INFO,
    actionTypes.EXTRA_SERVICES_ADDITONAL_INFO_SUCCESS,
    actionTypes.EXTRA_SERVICES_ADDITONAL_INFO_FAIL,
  ];
  return {
    types,
    promise: (client) => {
      const requests = [];
      if (services.MEDICARE && services.MEDICARE.isAvailable) {
        requests.push(client.get(`${API_HOST}/credit-application-pl/extra-services/MEDICARE`));
      }
      if (services.BILL_PROTECTOR && services.BILL_PROTECTOR.isAvailable) {
        requests.push(client.get(`${API_HOST}/credit-application-pl/extra-services/BILL_PROTECTOR`));
      }
      return Promise.all(requests);
    },
  };
}

export function getExtraServicesAvailabilityPL(loan, data) {
  const obj = {
    id: loan.selectedProduct.id,
    firstDrawAmount: toCents(data.firstDrawAmount) || 0,
  };

  const principal = loan.availablePrincipalsIndex[data.loanAmountIndex];
  const selectedProduct = find(loan.availableProducts, {
    principal,
    maturityPeriods: parseInt(data.selectedMaturity, 10),
  });
  obj.id = selectedProduct.id;
  obj.firstDrawAmount = principal;

  const types = [
    actionTypes.EXTRA_SERVICES_AVAILABILITY,
    actionTypes.EXTRA_SERVICES_AVAILABILITY_SUCCESS,
    actionTypes.EXTRA_SERVICES_AVAILABILITY_FAIL,
  ];

  return {
    types,
    data: obj,
    promise: (client) => client.put(`${API_HOST}/credit-application-pl/product`, obj)
      .then(() => client.get(`${API_HOST}/credit-application-pl/extra-services`)),
    afterSuccess: (dispatch, getState, response) => {
      dispatch(getExtraServicesAdditionalInfoPL(response.data));
    },
  };
}

export function toggleVas(serviceName) {
  return {
    type: actionTypes.VAS_TOGGLE,
    data: { serviceName },
  };
}

export function putMedicare(enabled, afterSuccess) {
  const URL = `${API_HOST}/credit-application-pl/extra-services/MEDICARE`;
  return {
    types: [
      actionTypes.PUT_MEDICARE,
      actionTypes.PUT_MEDICARE_SUCCESS,
      actionTypes.PUT_MEDICARE_FAIL,
    ],
    promise: (client) => client.put(URL, { enabled }),
    afterSuccess: (dispatch) => {
      if (typeof afterSuccess === 'function') {
        afterSuccess(dispatch);
      }
    },
  };
}

export function putBillProtector(enabled, afterSuccess) {
  const URL = `${API_HOST}/credit-application-pl/extra-services/BILL_PROTECTOR`;
  return {
    types: [
      actionTypes.PUT_BILL_PROTECTOR,
      actionTypes.PUT_BILL_PROTECTOR_SUCCESS,
      actionTypes.PUT_BILL_PROTECTOR_FAIL,
    ],
    promise: (client) => client.put(URL, { enabled }),
    afterSuccess: (dispatch) => {
      if (typeof afterSuccess === 'function') {
        afterSuccess(dispatch);
      }
    },
  };
}

export function putRepaymentPlan() {
  const URL = `${API_HOST}/credit-application/extra-services/REPAYMENT_PLAN`;
  const service = {
    enabled: true,
  };
  return {
    types: [
      actionTypes.PUT_REPAYMENT_PLAN,
      actionTypes.PUT_REPAYMENT_PLAN_SUCCESS,
      actionTypes.PUT_REPAYMENT_PLAN_FAIL,
    ],
    promise: (client) => client.put(URL, service),
  };
}

export function getLoanPurposes() {
  const URL = `${API_HOST}/credit-application-au/loan-purposes`;
  return {
    types: [
      actionTypes.GET_LOAN_PURPOSES,
      actionTypes.GET_LOAN_PURPOSES_SUCCESS,
      actionTypes.GET_LOAN_PURPOSES_FAIL,
    ],
    promise: (client) => client.get(URL),
  };
}

export function resetLoanForm() {
  return {
    type: actionTypes.RESET,
  };
}

export function redirectLogin() {
  return {
    type: actionTypes.REDIRECT_LOGIN,
  };
}

function getLoanStep(creditApplication, loan, persistentItem, authentication, skipProvisioPageAU) {
  const { steps } = loan;
  const { shouldShowConsentPage, authenticationMethod } = authentication;
  const productStep = getIndexStepByName(steps, 'PRODUCT');
  const hasProduct = creditApplication.product && creditApplication.product.id;

  if (shouldShowConsentPage && authenticationMethod !== 'IMPERSONATION') {
    return loan.currentStep;
  }

  if (creditApplication.metadata?.screen_name === STEP_ALMOST_THERE.type) {
    return getIndexStepByName(steps, STEP_ALMOST_THERE.type);
  }

  if (creditApplication.metadata?.screen_name === STEP_INFORMATION.type) {
    return getIndexStepByName(steps, STEP_INFORMATION.type);
  }

  if (creditApplication.metadata?.screen_name === STEP_SACC_LOADING.type) {
    return getIndexStepByName(steps, STEP_SACC_LOADING.type);
  }

  if (INCREASE_APP) {
    if (!hasProduct && authentication.increaseType === 'CASE_1') {
      return productStep;
    }
  }

  if (creditApplication.metadata && !hasProduct) {
    const impersonationAU = authentication.metadata && authentication.metadata.stepName === 'CompleteRegistration';
    const { STEP_NAME } = creditApplication.metadata;
    const cookiesManager = getCookiesManager();
    const OCMEmailConfirmed = cookiesManager.getBoolValue('ocmEmailConfirmed', COUNTRY);
    const wasOnEMailVerificationPage = authentication.screenName === '';
    const emailVerified = authentication.emailPending === null
      || authentication.emailPending === authentication.email;
    if (creditApplication.channel === 'OCM-WEB' && !OCMEmailConfirmed && (wasOnEMailVerificationPage || !emailVerified)) {
      const newSteps = [...steps];
      const emailConfirmation = [{
        type: 'CONFIRM_EMAIL',
        path: 'confirm-email',
        analyticsStepName: '1',
      }];
      const impersonationAuStepIndex = newSteps.findIndex((step) => step.type === 'IMPERSONATION_AU');
      newSteps.splice((impersonationAuStepIndex), 1, ...emailConfirmation);
      store.dispatch(setSteps(newSteps));
      if (!emailVerified && !OCMEmailConfirmed) {
        const nextStep = newSteps.findIndex((step) => step.type === emailConfirmation[0].type);
        return nextStep;
      }
    } else if (STEP_NAME) {
      return steps.findIndex((item) => item.type === STEP_NAME) + 1;
    } else if (!impersonationAU) {
      let nextStep = getIndexStepByName(steps, 'CREDIT_SENSE');
      if (skipProvisioPageAU) {
        nextStep = getIndexStepByName(steps, 'ADDRESS_AU');
      }
      return nextStep;
    }
  }

  if (hasProduct) {
    if (
      creditApplication.product.type === 'CREDIT_LINE'
      || (
        creditApplication.product.type === 'INSTALLMENT'
        && creditApplication.metadata
        && creditApplication.metadata.STEP_NAME === 'LOAN_PURPOSE'
      )
    ) {
      return steps.findIndex((item) => item.type === 'SUBMIT');
    }

    if (creditApplication.metadata.STEP_NAME === STEP_ALMOST_THERE.type) {
      return steps.findIndex((item) => item.type === STEP_ALMOST_THERE.type);
    }

    return productStep + 1;
  }

  if (COUNTRY === 'ES' && !hasProduct) {
    if (!hasProduct) {
      const fistScoringStepIndex = steps.findIndex((step) => step.type === 'SCORING_STATUS');
      if (creditApplication.scoringId) {
        return fistScoringStepIndex;
      }

      const scoringStep = steps[fistScoringStepIndex];
      const isLoanQuestionSubmitted = creditApplication.financialData
        && creditApplication.financialData.appliedForAmount;
      if (!creditApplication.scoringId && isLoanQuestionSubmitted) {
        store.dispatch(setRescoringToDoForOpenCreditApplication(1));
        const newSteps = [...steps];
        const kontomatikStepIndex = newSteps.findIndex((step) => step.type === 'KONTOMATIK');
        const kontomatikStep = newSteps[kontomatikStepIndex];
        pullAt(newSteps, kontomatikStepIndex);
        const isLoanPurposeStepSubmitted = creditApplication.financialData
          && creditApplication.financialData.loanPurpose;
        const isFamilyStepSubmitted = creditApplication.financialData
          && creditApplication.financialData.maritalStatus;
        if (isLoanPurposeStepSubmitted && isFamilyStepSubmitted) {
          pullAt(newSteps, fistScoringStepIndex);
          const secondScoringStepIndex = newSteps.findIndex((step) => step.type === 'SCORING_STATUS');
          newSteps.splice(secondScoringStepIndex + 1, 0, kontomatikStep);
          newSteps.splice(secondScoringStepIndex + 2, 0, scoringStep);
          store.dispatch(setSteps(newSteps));
          return secondScoringStepIndex;
        }
        newSteps.splice(fistScoringStepIndex + 1, 0, kontomatikStep);
        store.dispatch(setSteps(newSteps));
        return fistScoringStepIndex;
      }
    }

    const { isApplyGuarantor } = authentication;

    if (!PARTNER_PORTAL
      && !isApplyGuarantor
      && loan.currentStep > 0
    ) {
      return loan.currentStep;
    }
  }

  if (COUNTRY === 'FI' && creditApplication.financialData && creditApplication.financialData.salary) {
    return productStep;
  }

  if (COUNTRY === 'PL') {
    if (creditApplication.financialData && creditApplication.financialData.bankAccountDuration) {
      return productStep;
    }
  }

  // EE Impersonation should always start with email & channel step
  if (COUNTRY === 'EE' && authentication.authenticationMethod === 'IMPERSONATION') {
    return steps.findIndex((item) => item.type === 'CUSTOMER');
  }
  if (creditApplication.financialData && creditApplication.financialData.netIncome) {
    if (COUNTRY === 'EE' && !creditApplication.financialData.loanPurpose) {
      return steps.findIndex((item) => item.type === STEP_SYSTEM_AUTHORITY.type);
    }
    return productStep;
  }

  return 1;
}

const removeRedundantOccupationInformationMX = (data) => {
  const cleanData = { ...data };
  const workingStateType = workingStateTypes.find((item) => item.type === cleanData.occupation)
    || {};
  const isOtherUniversity = data.studiesAtInstitution === 'OTHER';
  if (!workingStateType.isCompanyNeeded && cleanData.company) {
    delete cleanData.company;
  }
  if (!workingStateType.isEmploymentDurationNeeded && cleanData.employmentDuration) {
    delete cleanData.employmentDuration;
  }
  if (!workingStateType.isActivityTypeNeeded && cleanData.activityType) {
    delete cleanData.activityType;
  }
  if (!workingStateType.isIndustryNeeded && cleanData.industry) {
    delete cleanData.industry;
  }
  if (!workingStateType.isRetirementTypeNeeded && cleanData.retirementPensionedBy) {
    delete cleanData.retirementPensionedBy;
  }
  if (!workingStateType.isUniversityNeeded && cleanData.studiesAtInstitution) {
    delete cleanData.studiesAtInstitution;
  }
  if (!isOtherUniversity && cleanData.studiesAtOtherInstitution) {
    delete cleanData.studiesAtOtherInstitution;
  }
  if (!workingStateType.isFreelancersIndustryNeeded && cleanData.freelancersIndustry) {
    delete cleanData.freelancersIndustry;
  }
  return cleanData;
};

export function getProductName() {
  return {
    types: [
      actionTypes.GET_PRODUCT_NAME,
      actionTypes.GET_PRODUCT_NAME_SUCCESS,
      actionTypes.GET_PRODUCT_NAME_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application-au`),
  };
}

export function getGovBenefits() {
  return {
    types: [
      actionTypes.GET_GOV_BENEFITS_VALUE,
      actionTypes.GET_GOV_BENEFITS_VALUE_SUCCESS,
      actionTypes.GET_GOV_BENEFITS_VALUE_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application-au/bank-scraping-metrics`,
      { hideError: true }
    ),
  };
}

export function shouldShowSACCPage() {
  const { loan } = store.getState();

  const isGovernmentBenefits = loan.customer?.creditApplication?.governmentBenefits?.amount > 0
    || loan.customer?.governmentBenefits > 0
    || loan.financialData?.GOVERNMENT_BENEFITS;
  const isSACC = loan.selectedProduct.isSACC;

  return {
    type: actionTypes.SHOULD_SHOW_SACC_PAGE,
    data: isGovernmentBenefits && isSACC,
  };
}

const putProductActionAU = (data, loan) => {
  let obj;
  let selectedProduct;
  const { principalIndex, firstDrawAmountIndex } = data;
  if (INCREASE_APP) {
    if (loan.productType === 'INSTALLMENT') {
      const { selectedMaturity } = data;
      const productOffer = loan.upsell.products[principalIndex].productOffers[selectedMaturity];
      obj = {
        id: productOffer.productId,
      };
      selectedProduct = {
        ...obj,
        principal: parseInt(productOffer.loanAmount, 10),
        maturityPeriods: parseInt(productOffer.monthsLoanDuration, 10),
        productType: 'INSTALLMENT',
      };
    } else if (loan.productType === 'CREDIT_LINE') {
      const upgradeProduct = loan.upgrade.products.find((product) =>
        product.creditAmount === data.creditLimit);
      obj = {
        id: parseInt(upgradeProduct.productId, 10),
        firstDrawAmount: 0,
      };
      selectedProduct = {
        ...obj,
        productType: 'CREDIT_LINE',
      };
    }
  } else {
    const { availableProducts, availablePrincipalsIndex, productType } = loan;
    const product = availableProducts[availablePrincipalsIndex[principalIndex]];
    const firstDrawAmountFromIndex = product.firstDrawAmountIndexes[firstDrawAmountIndex];
    if (productType === 'INSTALLMENT') {
      const duration = product.productOffers[firstDrawAmountFromIndex];
      obj = {
        id: parseInt(duration.productId, 10),
      };
      selectedProduct = {
        ...obj,
        principal: parseInt(product.loanAmount, 10),
        maturityPeriods: parseInt(duration.duration, 10),
        productType: 'INSTALLMENT',
      };
      obj.firstDrawAmount = selectedProduct.principal;
    } else {
      const withdraw = product.withdraws[firstDrawAmountFromIndex];
      obj = {
        id: parseInt(product.productId, 10),
        firstDrawAmount: parseInt(withdraw.withdrawAmount, 10),
      };
      selectedProduct = {
        ...obj,
        principal: parseInt(product.creditAmount, 10),
        productType: 'CREDIT_LINE',
      };
    }
  }

  const URL = `${API_HOST}/credit-application-au/product-offer`;
  return {
    types: [
      actionTypes.PUT_PRODUCT,
      actionTypes.PUT_PRODUCT_SUCCESS,
      actionTypes.PUT_PRODUCT_FAIL,
    ],
    promise: (client) => client.put(URL, obj, { selectedProduct }),
    afterSuccess: async (dispatch) => {
      await dispatch(getProductName());
      await dispatch(getGovBenefits());
      dispatch(shouldShowSACCPage());
    },
  };
};

const postBankStatementAction = (data) => ({
  types: [
    actionTypes.POST_BANK_STATEMENTS,
    actionTypes.POST_BANK_STATEMENTS_SUCCESS,
    actionTypes.POST_BANK_STATEMENTS_FAIL,
  ],
  promise: (client) => {
    if (COUNTRY === 'CZ') {
      return data.bankStatement.reduce((chain, curr) => {
        const formData = new FormData();
        formData.append('file', curr);
        return chain.then(() => client.post(
          `${API_HOST}/file/upload-document`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        ));
      }, Promise.resolve());
    }
    if (COUNTRY === 'ES') {
      const fileTypes = ['IBAN_PROOF', 'INCOME_PROOF', 'PASSPORT_A', 'PASSPORT_B', 'SELFIE'];
      const filesData = Object.keys(data)
        .filter((key) => fileTypes.includes(key))
        .reduce((obj, key) => ({
          ...obj,
          [key]: data[key],
        }), {});
      return Object.keys(filesData).reduce((chain, key) =>
        chain.then(() => (
          filesData[key].reduce((fileChain, file) => {
            const formData = new FormData();
            formData.append('file', file);
            return fileChain.then(() => client.post(
              `${API_HOST}/file/upload/${key}`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            ));
          }, Promise.resolve())
        )), Promise.resolve());
    }
    const files = data.bankStatement.map((file) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.post(
        `${API_HOST}/file/upload-bankstatement`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    });
    return Promise.all(files);
  },
});

function initLoanStep(creditApplication, loan, persistentItem, authentication, skipProvisioPageAU) {
  const data = getLoanStep(
    creditApplication,
    loan,
    persistentItem,
    authentication,
    skipProvisioPageAU
  );

  return {
    type: actionTypes.INIT_LOAN_STEP,
    data,
  };
}

export function startBluemediaTransaction() {
  return {
    types: [
      actionTypes.START_BLUEMEDIA,
      actionTypes.START_BLUEMEDIA_SUCCESS,
      actionTypes.START_BLUEMEDIA_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/payin/bluemedia-start-transaction?frontendVersion=WEB_2_0`,
      { hideError: true }
    ).then((response) => {
      if (EMBEDDED) {
        return response;
      }
      const params = queryStringParser.parse(response.data.payload);
      const bluemediaForm = document.createElement('form');
      bluemediaForm.setAttribute('method', 'post');
      bluemediaForm.setAttribute('action', response.data.url);
      Object.keys(params).forEach((key) => {
        const newTextbox = document.createElement('input');
        newTextbox.type = 'hidden';
        newTextbox.name = key;
        newTextbox.value = params[key];
        bluemediaForm.appendChild(newTextbox);
      });
      document.body.appendChild(bluemediaForm);
      bluemediaForm.submit();
      return null;
    }),
  };
}

export function getRegistrationStatus() {
  return {
    types: [
      actionTypes.GET_REGISTRATION_STATUS,
      actionTypes.GET_REGISTRATION_STATUS_SUCCESS,
      actionTypes.GET_REGISTRATION_STATUS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application-pl/registration-status`,
      { hideError: true }
    ),
  };
}

export function getPaymentPlan() {
  return {
    types: [
      actionTypes.GET_PAYMENT_PLAN,
      actionTypes.GET_PAYMENT_PLAN_SUCCESS,
      actionTypes.GET_PAYMENT_PLAN_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application/paymentplan`,
      { hideError: true, preventLogout: true }
    ),
  };
}

export function triggerEmail() {
  return {
    types: [
      actionTypes.TRIGGER_EMAIL,
      actionTypes.TRIGGER_EMAIL_SUCCESS,
      actionTypes.TRIGGER_EMAIL_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/credit-application/send-registration-email`,
      { hideError: true, preventLogout: true }
    ),
  };
}

function initESPartnerPortalFields(dispatch, authentication, loan) {
  let dob = authentication.dateOfBirth;
  let birthDay = '';
  let birthMonth = '';
  let birthYear = '';
  if (dob) {
    dob = dob.split('-');
    birthYear = dob[0];
    birthMonth = dob[1];
    birthDay = dob[2];
  }

  const { financialData } = loan;
  const { address } = authentication;
  const street = address.street ? address.street.split(' ') : [];

  const formInitialValues = {
    ...initialState.formInitalValues,
    birthDay,
    birthMonth,
    birthYear,
    firstName: authentication.firstName || '',
    lastName: authentication.lastName || '',
    secondLastName: authentication.secondLastName || '',
    countryOfBirth: authentication.countryOfBirth || '',
    email: authentication.email || authentication.emailPending || '',
    channel: loan.channel || '',
    education: financialData.education || '',
    occupation: financialData.occupation || '',
    employmentDuration: financialData.employmentDuration || '',
    loanPurpose: financialData.loanPurpose || '',
    maritalStatus: financialData.maritalStatus || '',
    netIncome: financialData.netIncome ? `${fromCents(financialData.netIncome)}` : '',
    street: street.length > 0 ? street.slice(0, street.length - 1).join(' ') : '',
    streetNumber: street.length > 0 ? street[street.length - 1] : '',
    postcode: address.postcode || '',
    city: address.city || '',
    province: address.province || '',
  };

  setFieldsValues(dispatch, formInitialValues);
}

export function pushGaPurchase() {
  return {
    type: actionTypes.PUSH_GA_PURCHASE,
    meta: {
      analyticsLV: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
      analyticsFI: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
      analyticsEE: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
      analyticsPL: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
      analyticsAU: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
      analyticsMX: (storeState) => (
        getAnalyticsObject('purchase', storeState)
      ),
    },
  };
}

export function pushGaStepEvent(step, data) {
  return {
    type: actionTypes.PUSH_GA_STEP,
    meta: {
      analyticsAU: (storeState) => getAnalyticsObjectAU(`${step}`, storeState, data),
    },
  };
}

export function instantorFailed() {
  return {
    type: actionTypes.INSTANTOR_FAILED,
  };
}

export function instantorSuccess() {
  return {
    type: actionTypes.INSTANTOR_SUCCESS,
    meta: {
      analyticsLV: (storeState) => getAnalyticsObject('verificationBank', storeState),
    },
  };
}

export function getContractInterestValue(contractId) {
  return {
    types: [
      actionTypes.GET_CONTRACT_INTEREST_VALUE,
      actionTypes.GET_CONTRACT_INTEREST_VALUE_SUCCESS,
      actionTypes.GET_CONTRACT_INTEREST_VALUE_FAIL,
    ],
    promise: (client) => {
      const url = `${API_HOST}/contracts/${contractId}`;
      const interestValue = client.get(url, { hideError: true })
        .then((res) => res.data)
        .then((data) => data.product.interest)
        .catch((error) => console.log(error));
      return interestValue;
    },
  };
}

export function setProvisioSkipAU(data) {
  return {
    type: actionTypes.SET_PROVISIO_SKIP_AU,
    data,
  };
}

export function submitInitLoanStep(channel, frontPagePrincipal, contractId, mainApplicationId) {
  let data = {};
  let channelData = {};
  let skipProvisioPageAU = false;

  if (COUNTRY === 'ES') {
    // Spain is handled separately because of PXLM-295
    if (channel) {
      channelData = { channel };
    }

    if (mainApplicationId) {
      channelData = {
        ...channel,
        mainApplicationId,
      };
    }
  } else if (COUNTRY !== 'AU') {
    data = {
      channel: MOBILE_WALLET ? 'Mobile-Wallet' : 'loan_app',
      isChannelDefault: true,
    };
    if (COUNTRY !== 'PL') {
      if (channel) {
        channelData = { channel };
        data.channel = channel;
        data.isChannelDefault = false;
      } else {
        channelData = { channel: data.channel };
      }
      if (VARIANT_FI_ZERO_DRAW) {
        channelData.journey = 'apply-n';
      }
      if (COUNTRY === 'CZ') {
        if (frontPagePrincipal) {
          channelData.frontPagePrincipal = frontPagePrincipal;
        }
      }
    }
  }
  return {
    types: [
      actionTypes.INIT_LOAN,
      actionTypes.INIT_LOAN_SUCCESS,
      actionTypes.INIT_LOAN_FAIL,
    ],
    data,
    promise: (client) => {
      let url = `${API_HOST}/${creditApplicationInitPath()}`;
      const { authentication: { authenticationMethod } } = store.getState();
      const creditApplicationRequest = (
        // eslint-disable-next-line no-nested-ternary
        COUNTRY === 'AU' ? (
          authenticationMethod === 'IMPERSONATION'
            ? client.get(`${API_HOST}/bank-statement/is-valid`, { hideError: true })
              .then((response) => {
                skipProvisioPageAU = response.data.isValid;
                store.dispatch(setProvisioSkipAU(skipProvisioPageAU));
                return client.get(url, { hideError: true });
              })
            : client.get(url, { hideError: true })
              .then((userData) => {
                client.get(`${API_HOST}/bank-statement/is-valid`, { hideError: true })
                  .then((response) => {
                    skipProvisioPageAU = response.data.isValid;
                    store.dispatch(setProvisioSkipAU(skipProvisioPageAU));
                  });

                return userData;
              })
              // eslint-disable-next-line consistent-return
              .then(null, async (error) => {
                // eslint-disable-next-line max-len
                // Returning user will get 404 error when fetching data in line 2017
                // That's why we need to handle this error in given way
                // We need to change get method to the put to fix that
                if (error.status === 404) {
                  await client.put(url, { hideError: true })
                    .then(async (returningUserData) => {
                      await client.get(`${API_HOST}/bank-statement/is-valid`, { hideError: true })
                        .then((response) => {
                          // eslint-disable-next-line max-len
                          skipProvisioPageAU = response.data.isValid;// eslint-disable-next-line max-len
                          store.dispatch(setProvisioSkipAU(skipProvisioPageAU));
                        });

                      return Promise.resolve(returningUserData);
                    });

                  return Promise.reject(error);
                }
              })
        )
          : client.get(url, { hideError: true }))
        .then(null, async (error) => {
          if (error.status === 404) {
            const config = {
              shouldHideErrorForResponse: (response) => (
                (response.data && response.data.statusCode === 'CREDIT_REJECTION')
                || (COUNTRY === 'FI' && response.status === 412)
              ),
            };
            let requests;
            if (COUNTRY === 'ES' && mainApplicationId) {
              url += '/guarantor';
              requests = [client.post(url, channelData, config)];
            } else {
              const isCrediteaLV = window.location.href.indexOf('creditea.lv') !== -1;
              if (COUNTRY === 'LV' && isCrediteaLV) {
                config.headers = { 'X-Sms-Channel': 'Creditea' };
              }
              requests = [await client.put(url, channelData, config)];
            }
            if (COUNTRY === 'LT') {
              requests.push(await client.put(`${API_HOST}/customer/registration`, {}));
            }
            if (PARTNER_PORTAL && COUNTRY === 'ES') {
              requests.push(client.get(`${API_HOST}/credit-application/previous`, { hideError: true })
                .catch((exception) => {
                  if (DEBUG) {
                    console.log(exception);
                  }
                  return Promise.resolve({ data: {} });
                }));
            }
            return Promise.all(requests);
          }
          return Promise.reject(error);
        });
      if (INCREASE_APP) {
        return Promise.all([
          creditApplicationRequest,
          client.get(`${API_HOST}/contracts/${contractId}`, { hideError: true }),
        ]);
      }
      return creditApplicationRequest;
    },
    afterSuccess: (dispatch, getState, response) => {
      if (response.data && COUNTRY === 'CZ') {
        const {
          // eslint-disable-next-line camelcase -- node upgrade
          selected_monthly_payment_period,
          // eslint-disable-next-line camelcase -- node upgrade
          selected_monthly_payment_value,
        } = response.data.metadata;

        dispatch(change('loan', 'monthlyPaymentInCents', parseInt(selected_monthly_payment_value, 10)));
        dispatch(change('loan', 'selectedMaturity', parseInt(selected_monthly_payment_period, 10)));
      }

      const currentState = getState();
      const { loan, authentication } = currentState;
      const loanData = isArray(response) ? response[0].data : response.data;
      if (COUNTRY === 'LT' && loanData.channel === 'OCM-WEB' && loanData.state === 'OPEN') {
        redirectToSS();
      }
      loan.isApplicationWithProduct = !!loanData.product;
      const { financialData } = loanData;
      let savedAppStateId = authentication.identifier;
      if (COUNTRY === 'AU' && !INCREASE_APP) {
        const { metadata: { PRODUCT_TYPE_ON_CREATION } = {} } = loanData;
        savedAppStateId = authentication.id;
        if (PRODUCT_TYPE_ON_CREATION && PRODUCT_TYPE_ON_CREATION !== loan.productType) {
          let url = '/au/apply/';
          if (PRODUCT_TYPE_ON_CREATION === 'INSTALLMENT') {
            url = '/au/apply-il/';
          }
          window.location.href = url;
        }
      }
      const savedState = getSavedAppState(savedAppStateId);
      const isSavedDataExist = savedState && savedState.loan && savedState.loanFormValues;
      let isFinancialDataNotSent = isEmpty(financialData);
      let isEnoughValues = isSavedDataExist
        && !isEqual(initialState.formInitalValues, savedState.loanFormValues);
      if (COUNTRY === 'LT') {
        dispatch(getUserSession());
        if (!isFinancialDataNotSent) {
          isFinancialDataNotSent = !('monthlyObligations' in financialData);
        }
      }
      let isFilledByBroker = false;
      let isFilledByBrokerPayLater = false;
      let brokerUrl = null;
      if (COUNTRY === 'LT') {
        // all customer will use screeName to restore proper step
        isFinancialDataNotSent = true;
        isEnoughValues = false;
      }
      if (COUNTRY === 'MX') {
        const persistentItemByCustomerIdentifier = getPersistentItem(savedAppStateId);
        isFilledByBroker = (
          authentication.authenticationMethod === 'IMPERSONATION'
          && persistentItemByCustomerIdentifier
          && persistentItemByCustomerIdentifier.isFilledByBroker
        );
        isFilledByBrokerPayLater = (
          authentication.authenticationMethod === 'IMPERSONATION'
          && persistentItemByCustomerIdentifier
          && persistentItemByCustomerIdentifier.isFilledByBrokerPayLater
        );
        brokerUrl = persistentItemByCustomerIdentifier.brokerUrl;
        dispatch(setBrokerUrl(brokerUrl));

        if (
          !authentication.bankAccount
          && !authentication.paymentCardNumber
          && authentication.metadata.temp_payment_card
        ) {
          dispatch(change('loan', 'bankAccountDebitCard', authentication.metadata.temp_payment_card));
        }
        // all customer will use screeName to restore proper step
        isFinancialDataNotSent = true;
        isEnoughValues = false;
      }

      const persistentItemByLoanId = getPersistentItem(loan.id);

      if (PARTNER_PORTAL && COUNTRY === 'ES') {
        let loanESPP = {
          ...loan,
        };
        if (isArray(response) && response.length > 1) {
          loanESPP = {
            ...loanESPP,
            ...response[1].data,
          };
        }
        initESPartnerPortalFields(dispatch, authentication, loanESPP);
      }

      if (isSavedDataExist) {
        savedState.loan.id = loanData.id;
      }

      // in case that app was rejected, do not restore current step from savedState;
      if (COUNTRY === 'LT' && isSavedDataExist && savedState.loan.rejected !== loan.rejected) {
        isEnoughValues = false;
        isFinancialDataNotSent = false;
      }
      if (isFinancialDataNotSent && isEnoughValues) {
        const offerPageIndex = findIndex(savedState.loan.steps, (item) => item.type === 'PRODUCT');
        // @TODO: We have to get information if financial data is sent or not
        // from server, rather localStorage
        if (
          savedState.loan.currentStep < offerPageIndex
          && !(COUNTRY === 'EE' && authentication.authenticationMethod === 'IMPERSONATION')
        ) {
          // restore loan state (current step, ...) only if client didn't saw offer yet
          // otherwise he will start from first step again, only inputs will be prefilled
          //
          // For EE Impersonation always redirect to the first loan step
          if (COUNTRY === 'MX') {
            savedState.loan.session_uuid = loanData.session_uuid;
          }
          dispatch(restoreLoanState(savedState));
        } else {
          dispatch(
            // eslint-disable-next-line max-len
            initLoanStep(loanData, loan, persistentItemByLoanId, authentication, skipProvisioPageAU)
          );
        }

        const loanFormValues = getLoanFormValues(savedState.loanFormValues, authentication);

        restoreLoanFormValues(dispatch, loanFormValues);
      } else {
        let formInitialValues = initialState.formInitalValues;
        if (COUNTRY === 'MX') {
          if (isFilledByBroker || isFilledByBrokerPayLater) {
            formInitialValues = getBrokerAppPrefilledValues(
              formInitialValues,
              authentication,
              loanData
            );
          } else {
            formInitialValues = {
              ...formInitialValues,
              ...getMXPrefilledValues(authentication, loanData),
            };
          }
        }
        if (COUNTRY === 'ES') {
          if (loan.isFilledByBroker) {
            formInitialValues = getBrokerAppPrefilledValues(
              formInitialValues,
              authentication,
              loanData
            );
          }
        }
        if (COUNTRY === 'AU') {
          formInitialValues = getAUPrefilledValues(authentication, loan);
        }
        if (COUNTRY === 'EE') {
          if (savedState && savedState.loanFormValues) {
            const { firstDrawAmount, selectedMaturity, creditLine } = savedState.loanFormValues;
            const { currentStep, chooseNordigenConfirm } = savedState.loan;
            formInitialValues = {
              ...formInitialValues,
              firstDrawAmount,
              selectedMaturity,
              creditLine,
              currentStep,
              chooseNordigenConfirm,
            };
          }
        }
        if (COUNTRY === 'LV' && VARIANT_B) {
          dispatch(restoreLoanState(savedState));
        }
        dispatch(
          // eslint-disable-next-line max-len
          initLoanStep(loanData, loan, persistentItemByLoanId, authentication, skipProvisioPageAU)
        );

        setFieldsValues(dispatch, formInitialValues);
        if (COUNTRY === 'MX') {
          dispatch(saveLoanForm());
        }
        // dispatch(initialize('loan', initialState.formInitalValues));
      }
      if (COUNTRY === 'LV' && loanData.product && loanData.state === 'PENDING') {
        dispatch(getRegisterData());
      }

      const { oneCentStatus } = authentication;

      if (COUNTRY === 'LT') {
        if (oneCentStatus && oneCentStatus === 'NOT_STARTED_YET') {
          dispatchInitTmxSessionEvent(dispatch, loan);
        }
      } else {
        dispatchInitTmxSessionEvent(dispatch, loan);
      }

      if (COUNTRY === 'MX' || COUNTRY === 'ES' || COUNTRY === 'LT' || COUNTRY === 'CZ') {
        dispatch(verificationStateRestore(persistentItemByLoanId));
      }

      if (COUNTRY === 'ES') {
        const { creditApplicationId } = loanData;
        if (creditApplicationId) {
          dispatch(setCreditApplicationId(creditApplicationId));
        }
      }
    },
    afterFailure: (dispatch, getState, response) => {
      const { authentication: { id } } = getState();
      const message = constructErrorMessage(id, response);
      dispatch(putLog(message, 'error'));
    },
  };
}

const preapreLoanAfterCreditApplicationRequest = (dispatch, getState, response) => {
  const { loan, authentication } = getState();
  const { authenticationMethod } = authentication;
  const loanData = response.data;
  loan.isApplicationWithProduct = !!loanData.product;

  const { financialData } = loanData;
  const savedAppStateId = authentication.identifier;
  const savedState = getSavedAppState(savedAppStateId);
  const isSavedDataExist = savedState && savedState.loan && savedState.loanFormValues;
  const isFinancialDataNotSent = isEmpty(financialData);
  const isEnoughValues = isSavedDataExist
    && !isEqual(initialState.formInitalValues, savedState.loanFormValues);

  const persistentItemByLoanId = getPersistentItem(loan.id);

  if (isSavedDataExist) {
    savedState.loan.id = loanData.id;
  }

  if (isFinancialDataNotSent && isEnoughValues) {
    const offerPageIndex = findIndex(
      savedState.loan.steps,
      (item) => item.type === STEP_PRODUCT.type
    );
    if (
      savedState.loan.currentStep < offerPageIndex
      && (authenticationMethod !== AUTH_METHOD_IMPERSONATION)
    ) {
      dispatch(restoreLoanState(savedState));
    } else {
      dispatch(initLoanStep(loanData, loan, persistentItemByLoanId, authentication));
    }
    const loanFormValues = getLoanFormValues(savedState.loanFormValues, authentication);
    restoreLoanFormValues(dispatch, loanFormValues);
  } else {
    let formInitialValues = initialState.formInitalValues;
    if (savedState && savedState.loanFormValues) {
      const { firstDrawAmount, selectedMaturity, creditLine } = savedState.loanFormValues;
      const { currentStep, chooseNordigenConfirm } = savedState.loan;
      formInitialValues = {
        ...formInitialValues,
        firstDrawAmount,
        selectedMaturity,
        creditLine,
        currentStep,
        chooseNordigenConfirm,
      };
    }
    if (authenticationMethod !== AUTH_METHOD_ID_CARD) {
      dispatch(initLoanStep(loanData, loan, persistentItemByLoanId, authentication));
    }

    setFieldsValues(dispatch, formInitialValues);
  }
  dispatchInitTmxSessionEvent(dispatch, loan);
};

const getChannelDataForMarket = (channel, frontPagePrincipal = null) => {
  // In future check how to handle on AU market
  const channelData = {
    channel: channel || MOBILE_WALLET ? CHANNEL_MOBILE_WALLET : CHANNEL_LOAN_APP,
  };

  if (VARIANT_FI_ZERO_DRAW) {
    channelData.journey = CHANNEL_JOURNEY_APPLY_N;
  }

  if (COUNTRY === 'CZ' && frontPagePrincipal) {
    channelData.frontPagePrincipal = frontPagePrincipal;
  }

  return channelData;
};

export function startReidentifyCustomer() {
  return {
    type: actionTypes.START_REIDENTIFY_CUSTOMER,
  };
}

export function registerCustomer(data) {
  const url = `${API_HOST}/customer/registration`;
  const { authentication } = store.getState();
  const { authenticationMethod } = authentication;
  const communicationSettings = {
    contractDeliveryMethod: 'EMAIL',
    invoiceDeliveryMethod: 'EMAIL',
    preDueDateReminderSMS: true,
  };
  const regData = {
    email: { email: data.email },
    communicationSettings,
    preferredLanguage: getLanguage(),
  };
  if (data.msisdn && !data.password) {
    if (authenticationMethod === AUTH_METHOD_MOBILE_ID) {
      delete regData.msisdnVerification;
    }
  }
  if (authenticationMethod === AUTH_METHOD_BANK
    || authenticationMethod === AUTH_METHOD_ID_CARD
    || authenticationMethod === AUTH_METHOD_MOBILE_ID) {
    regData.password = {
      password: `${Math.random().toString(36).substr(2, 19)}1`,
    };
  }
  return {
    types: [
      actionTypes.FINISH_REIDENTIFY_CUSTOMER,
      actionTypes.FINISH_REIDENTIFY_CUSTOMER_SUCCESS,
      actionTypes.FINISH_REIDENTIFY_CUSTOMER_FAIL,
    ],
    data,
    promise: (client) => client.put(url, regData),
    afterSuccess: (dispatch, getState) => {
      dispatch(putMarketingConsents(getState(), data));
    },
  };
}

export function createCreditApplication(channelData) {
  const url = `${API_HOST}/${creditApplicationInitPath()}`;

  return {
    types: [
      actionTypes.INIT_CREDIT_APPLICATION,
      actionTypes.INIT_CREDIT_APPLICATION_SUCCESS,
      actionTypes.INIT_CREDIT_APPLICATION_FAIL,
    ],
    promise: (client) => client.put(url, channelData, { hideError: true }),
    afterSuccess: (dispatch, getState, response) =>
      preapreLoanAfterCreditApplicationRequest(dispatch, getState, response),
    afterFailure: (dispatch, getState, response) => {
      if (response.status === 409) {
        dispatch(startReidentifyCustomer());
      }
    },
  };
}

export function createCreditApplicationAfterReidentifyCustomer(channelData, registerData) {
  const url = `${API_HOST}/${creditApplicationInitPath()}`;

  return {
    types: [
      actionTypes.CREATE_CREDIT_APPLICATION_AFTER_REIDENTIFY,
      actionTypes.CREATE_CREDIT_APPLICATION_AFTER_REIDENTIFY_SUCCESS,
      actionTypes.CREATE_CREDIT_APPLICATION_AFTER_REIDENTIFY_FAIL,
    ],
    promise: (client) => client.put(url, channelData),
    afterSuccess: (dispatch, getState, response) => {
      preapreLoanAfterCreditApplicationRequest(dispatch, getState, response);
      dispatch(registerCustomer(registerData));
    },
  };
}

export function goToNextStep() {
  return {
    type: actionTypes.GO_TO_NEXT_STEP,
  };
}

export function checkIfUserNeedReidentify() {
  const url = `${API_HOST}/customer/identification/msisdn`;

  return {
    types: [
      actionTypes.CHECK_IF_USER_NEED_REIDENTIFY,
      actionTypes.CHECK_IF_USER_NEED_REIDENTIFY_SUCCESS,
      actionTypes.CHECK_IF_USER_NEED_REIDENTIFY_FAIL,
    ],
    promise: (client) => client.get(url),
    afterSuccess: (dispatch, getState, response) => {
      const { isValid } = response.data;
      const { authentication: { authenticationMethod, customerRegistered } } = getState();

      if (isValid || customerRegistered) {
        if (authenticationMethod === AUTH_METHOD_ID_CARD) {
          dispatch(goToNextStep());
        } else {
          const { authentication: { channel } } = getState();
          const channelData = getChannelDataForMarket(channel);
          dispatch(createCreditApplication(channelData));
        }
      } else {
        dispatch(startReidentifyCustomer());
      }
    },
  };
}

export function getCreditApplicationLoan(channel) {
  const url = `${API_HOST}/${creditApplicationInitPath()}`;
  const channelData = getChannelDataForMarket(channel);

  return {
    types: [
      actionTypes.GET_CREDIT_APPLICATION_LOAN,
      actionTypes.GET_CREDIT_APPLICATION_LOAN_SUCCESS,
      actionTypes.GET_CREDIT_APPLICATION_LOAN_FAIL,
    ],
    promise: (client) => client.get(url, { hideError: true }),
    afterSuccess: (dispatch, getState, response) =>
      preapreLoanAfterCreditApplicationRequest(dispatch, getState, response),
    afterFailure: (dispatch, getState, response) => {
      if (response.status === 404) {
        const { authentication } = getState();
        const { authenticationMethod, contracts } = authentication;
        const contractExist = contracts && contracts.length > 0;

        if (authenticationMethod !== AUTH_METHOD_ID_CARD && !contractExist) {
          dispatch(createCreditApplication(channelData));
        } else {
          dispatch(checkIfUserNeedReidentify());
        }
      } else if (response.status === 409) {
        dispatch(startReidentifyCustomer());
      } else {
        const { authentication: { id } } = getState();
        const message = constructErrorMessage(id, response);
        dispatch(putLog(message, 'error'));
      }
    },
  };
}

export function getMmpByDueDate(dueDate) {
  return {
    types: [
      actionTypes.GET_MMP_BY_DUE_DATE,
      actionTypes.GET_MMP_BY_DUE_DATE_SUCCESS,
      actionTypes.GET_MMP_BY_DUE_DATE_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}/credit-application/mmp`, { dueDate }),
  };
}

/**
 * @param {array} maturityPeriods - array of periods needed [6, 12, 36]
 * @param {number} productId - id of product
 */
export function getMMPsByMonth(maturityPeriods, productId) {
  const URL = `${API_HOST}/products/draw-selections-with-maturity-product-group`;
  return {
    types: [
      actionTypes.GET_MMPS_BY_MONTH,
      actionTypes.GET_MMPS_BY_MONTH_SUCCESS,
      actionTypes.GET_MMPS_BY_MONTH_FAIL,
    ],
    promise: (client) => {
      const periods = [];
      maturityPeriods.forEach((item) => {
        periods.push(client.get(`${URL}`, {
          params: {
            maturityPeriod: item,
            productId,
          },
          maturityPeriod: item,
          hideError: true,
        }));
      });
      return Promise.all(periods);
    },
  };
}

export const getFees = (productId) => {
  const URL = `${API_HOST}/credit-application-es/fees/${productId}`;
  return {
    types: [
      actionTypes.GET_FEES,
      actionTypes.GET_FEES_SUCCESS,
      actionTypes.GET_FEES_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
  };
};

export function getDueDateRange() {
  return {
    types: [
      actionTypes.GET_DATE_RANGE,
      actionTypes.GET_DATE_RANGE_SUCCESS,
      actionTypes.GET_DATE_RANGE_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application/duedate/range`),
    afterSuccess: (dispatch, getState, response) => {
      dispatch(
        change('loan', 'paymentDay', response.data.selectedDate)
      );
      const { loan } = getState();
      if (COUNTRY === 'EE') {
        if (loan.selectedProduct.firstDrawAmount > 0) {
          dispatch(getMmpByDueDate(response.data.selectedDate));
        }
      }
      if (COUNTRY === 'ES') {
        if (loan.steps[loan.currentStep].type === 'SUBMIT') {
          dispatch(getFees(loan.selectedProduct.id));
        }
      }
    },
  };
}

const loanPromise = (dataFirstDrawAmount, selectedProduct) => {
  const loanData = {
    firstDrawAmount: dataFirstDrawAmount
      ? toCents(dataFirstDrawAmount)
      : selectedProduct.firstDrawAmount,
  };
  const url = '/credit-application/submit';

  return {
    types: [
      actionTypes.SUBMIT,
      actionTypes.SUBMIT_SUCCESS,
      actionTypes.SUBMIT_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}${url}`, loanData),
    afterSuccess: (dispatch, getState) => {
      dispatch(pushGaPurchase());
      affiliateRequestContractSubmitted(dispatch, getState);
    },
  };
};

const migrationLoanPromise = (selectedProduct) => {
  const {
    loan: { products },
  } = store.getState();
  const url = '/credit-application/migration-submit';
  const currentProduct = products ? products[selectedProduct.principal] : null;

  if (!currentProduct) {
    return {
      type: actionTypes.SUBMIT_FAIL,
    };
  }
  const loanData = {
    firstDrawAmount: currentProduct.sum,
  };

  return {
    types: [
      actionTypes.SUBMIT,
      actionTypes.SUBMIT_SUCCESS,
      actionTypes.SUBMIT_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}${url}`, loanData),
    afterSuccess: (dispatch, getState) => {
      dispatch(pushGaPurchase());
      affiliateRequestContractSubmitted(dispatch, getState);
    },
  };
};

export function getPaymentPlanThenLoanPromise(dataFirstDrawAmount, selectedProduct) {
  return {
    types: [
      actionTypes.GET_PAYMENT_PLAN,
      actionTypes.GET_PAYMENT_PLAN_SUCCESS,
      actionTypes.GET_PAYMENT_PLAN_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application/paymentplan`,
      { hideError: true, preventLogout: true }
    ),
    afterSuccess: (dispatch) => {
      dispatch(loanPromise(dataFirstDrawAmount, selectedProduct));
    },
  };
}

function putAdditionalConsentActionPromise(name, value, consentType = name) {
  const URL = `${API_HOST}/customer/additional-consent/save-consent`;
  const requestBody = {
    name,
    value,
    consentType,
  };
  return (client) => client.put(URL, requestBody, { hideError: true });
}

export function putAdditionalConsent(name, value, consentType = name) {
  return {
    types: [
      actionTypes.PUT_ADDITIONAL_CONSENT,
      actionTypes.PUT_ADDITIONAL_CONSENT_SUCCESS,
      actionTypes.PUT_ADDITIONAL_CONSENT_FAIL,
    ],
    promise: putAdditionalConsentActionPromise(name, value, consentType),
  };
}

export function patchGuarantor(data, guarantorType, creditApplicationId) {
  return {
    types: [
      actionTypes.PATCH_GUARANTOR_ES,
      actionTypes.PATCH_GUARANTOR_ES_SUCCESS,
      actionTypes.PATCH_GUARANTOR_ES_FAIL,
    ],
    guarantorType,
    promise: (client) => client.patch(`${API_HOST}/credit-application-es/${creditApplicationId}/guarantor`, {
      creditApplicationId,
      guarantorType,
    }),
  };
}

export function patchGuarantorThenGetFees(data, guarantorType, creditApplicationId) {
  const rootState = store.getState();
  const { loan } = rootState;
  return {
    types: [
      actionTypes.PATCH_GUARANTOR_THEN_FEES_ES,
      actionTypes.PATCH_GUARANTOR_THEN_FEES_ES_SUCCESS,
      actionTypes.PATCH_GUARANTOR_THEN_FEES_ES_FAIL,
    ],
    guarantorType,
    promise: (client) => client.patch(`${API_HOST}/credit-application-es/${creditApplicationId}/guarantor`, {
      creditApplicationId,
      guarantorType,
    }).then((response) => store.dispatch(getFees(loan.selectedProduct.id)).then(() => response)),
  };
}

export function putProductES(data, guarantorType, creditApplicationId) {
  const URL = `${API_HOST}/credit-application/product`;
  const rootState = store.getState();
  const { loan } = rootState;
  const actionData = { firstDrawAmount: loan.selectedProduct.firstDrawAmount };
  const productData = {
    ...actionData,
    id: loan.selectedProduct.id,
  };
  return {
    types: [
      actionTypes.PUT_PRODUCT,
      actionTypes.PUT_PRODUCT_SUCCESS,
      actionTypes.PUT_PRODUCT_FAIL,
    ],
    data: actionData,
    promise: (client) => client
      .put(URL, productData)
      .then((response) =>
        store.dispatch(patchGuarantorThenGetFees(data, guarantorType, creditApplicationId))
          .then(() => response)),
    afterSuccess: (dispatch, getState) => {
      dispatch(putMarketingConsents(getState(), data));
    },
    meta: {},
  };
}

export function getAllPossibleMMPsByMonth(productId) {
  return {
    types: [
      actionTypes.GET_ALL_MMPS_BY_MONTH,
      actionTypes.GET_ALL_MMPS_BY_MONTH_SUCCESS,
      actionTypes.GET_ALL_MMPS_BY_MONTH_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/products/draw-selections-all-maturities-for-product?productId=${productId}`,
      { hideError: true }
    ),
  };
}

export function putMigrationProductES(data, guarantorType, creditApplicationId) {
  const URL = `${API_HOST}/credit-application/product`;
  const rootState = store.getState();
  const { loan } = rootState;
  const product = loan.products[toCents(data.loanAmountIndex)];
  const actionData = {
    product,
  };
  const productData = {
    id: product.id,
    firstDrawAmount: product.sum,
  };
  return {
    types: [
      actionTypes.PUT_MIGRATION_PRODUCT,
      actionTypes.PUT_MIGRATION_PRODUCT_SUCCESS,
      actionTypes.PUT_MIGRATION_PRODUCT_FAIL,
    ],
    data: actionData,
    promise: (client) => client
      .put(URL, productData)
      .then((response) =>
        store.dispatch(patchGuarantor(data, guarantorType, creditApplicationId))
          .then(() => response)),
  };
}

export function skipNordigenStep(value) {
  return {
    type: actionTypes.SKIP_NORDIGEN_STEP,
    data: value,
  };
}

export function setNordigenError(setError) {
  return {
    type: actionTypes.SET_NORDIGEN_ERROR,
    data: setError,
  };
}

export function putLoanPurpose(data, mainApplicationId) {
  const url = `${API_HOST}/credit-application/loanpurpose`;
  const {
    loanPurpose, appliedForAmount, mmp, netIncome, livingCosts,
  } = data;
  return {
    types: [
      actionTypes.PUT_LOAN_PURPOSE,
      actionTypes.PUT_LOAN_PURPOSE_SUCCESS,
      actionTypes.PUT_LOAN_PURPOSE_FAIL,
    ],
    data,
    promise: (client) => client.put(url, {
      loanPurpose: mainApplicationId ? 'GUARANTOR' : loanPurpose,
      appliedForAmount: toCents(removeNonDigitCharacters(appliedForAmount)),
      MMP: toCents(removeNonDigitCharacters(mmp)),
      livingCosts: toCents(removeNonDigitCharacters(livingCosts)),
      netIncome: toCents(removeNonDigitCharacters(netIncome)),
    }),
    afterSuccess: (dispatch, getState) => {
      const { rescoringToDoForOpenCreditApplication, scoringId } = getState();
      if (rescoringToDoForOpenCreditApplication && scoringId) {
        dispatch(getApplicationScoringStatus(scoringId));
      }
    },
  };
}

export function clearLoanValidationError() {
  return {
    type: actionTypes.CLEAR_LOAN_VALIDATION_ERROR,
  };
}

export function getPsychometricURLToIframe() {
  const { id } = store.getState().loan;

  const URL = `${API_HOST}/ia/init/${id}`;

  return {
    types: [
      actionTypes.GET_PSYCHOMETRIC_URL,
      actionTypes.GET_PSYCHOMETRIC_URL_SUCCESS,
      actionTypes.GET_PSYCHOMETRIC_URL_FAIL,
    ],
    promise: (client) => client.post(URL),
    afterSuccess: (dispatch) => {
      if (COUNTRY === COUNTRIES.MX) {
        dispatch(track('checkout', {
          analyticsMX: () => {
            const {
              authentication: { id: authenticationId, isOldCustomer },
              loan: { id: loanId },
            } = store.getState();

            return {
              event: 'checkout',
              page: STEP_PSYCHOMETRIC.routerPath,
              userID: authenticationId,
              applicationID: loanId,
              clientType: isOldCustomer ? 'Returning Client' : 'New Client',
            };
          },
        }));
      }
    },
  };
}

export function checkForTaxAuthorityData() {
  const url = `${API_HOST}/customer-ee/external-registry-income`;

  return {
    types: [
      actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA,
      actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA_SUCCESS,
      actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA_FAIL,
    ],
    promise: (client) => client.get(url, { hideError: true }),
    afterSuccess: (dispatch, getState, response) => {
      const { data: { requiredConsentsRedirectUrl } } = response;
      const consentsClickedBefore = localStorage.getItem(TAX_AUTHORITY_CONSENTS_CLICKED);

      if (!consentsClickedBefore && requiredConsentsRedirectUrl) {
        const {
          authentication: { id: customerId, isOldCustomer },
          loan: { id: applicationId },
        } = getState();

        dispatch(track('checkout', {
          analyticsEE: () => ({
            event: 'checkout',
            page: 'income-verification',
            userID: customerId,
            applicationID: applicationId,
            clientType: isOldCustomer ? 'Returning Client' : 'New Client',
          }),
        }));

        localStorage.setItem(TAX_AUTHORITY_CONSENTS_URL, requiredConsentsRedirectUrl);
      }
    },
  };
}

export function providentScoringMX(message) {
  const { loan } = store.getState();

  const URL = `${API_HOST}/provident/scoring/${loan.id}`;

  return {
    types: [
      actionTypes.PROVIDENT_SCORING_MX,
      actionTypes.PROVIDENT_SCORING_MX_SUCCESS,
      actionTypes.PROVIDENT_SCORING_MX_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
    afterSuccess: (dispatch, getState, response) => {
      const {
        riskGrade, noHitProductForRiskGrades, showPsychometricTest, score,
      } = response.data;

      const {
        authentication: { id: authenticationId, isOldCustomer },
        loan: { id: loanId },
      } = store.getState();

      const isRiskGradeInRange1To5 = riskGrade >= 1 && riskGrade <= 5;
      const {
        isOffer,
        isProvidentProduct,
        isPsychometric,
      } = getStepAfterProvidentScoring(
        riskGrade,
        score,
        showPsychometricTest,
        noHitProductForRiskGrades,
        message
      );

      if (isRiskGradeInRange1To5) {
        if (isOffer) {
          resetIsFilledByBroker(getState);
          dispatch(track('checkout', {
            analyticsMX: () => ({
              event: 'checkout',
              page: 'offer_no_hit',
              userID: authenticationId,
              applicationID: loanId,
              clientType: isOldCustomer ? 'Returning Client' : 'New Client',
            }),
          }));
        } else if (isProvidentProduct || isPsychometric) {
          dispatch(track('checkout', {
            analyticsMX: () => ({
              event: 'checkout',
              amountPresented: response.data.offer.amount,
              userID: authenticationId,
              applicationID: loanId,
              clientType: isOldCustomer ? 'Returning Client' : 'New Client',
              page: isProvidentProduct ? 'proviOffer' : 'psychometric',
            }),
          }));
        }
      }
    },
  };
}

export function providentUpdate() {
  const { loan } = store.getState();

  const providentOfferID = loan.providentScoring.offer.offerId;

  const URL = `${API_HOST}/provident/update/${providentOfferID}`;

  return {
    types: [
      actionTypes.PROVIDENT_UPDATE,
      actionTypes.PROVIDENT_UPDATE_SUCCESS,
      actionTypes.PROVIDENT_UPDATE_FAIL,
    ],
    promise: (client) => client.put(
      URL,
      {
        offerAccepted: true,
      },
      { hideError: true }
    ),
  };
}

export function submitLoanStep(type, data, loan) {
  if (DEBUG) {
    console.log('submit loan step: ', type, data);
  }
  switch (type) {
    case 'CUSTOMER': {
      const { authentication } = store.getState();
      const { authenticationMethod, channel } = authentication;
      const { id } = loan;

      const communicationSettings = {
        contractDeliveryMethod: 'EMAIL',
        invoiceDeliveryMethod: 'EMAIL',
        preDueDateReminderSMS: true,
      };
      const regData = {
        email: { email: data.email },
        communicationSettings,
        preferredLanguage: getLanguage(),
      };
      if (data.msisdn && !data.password) {
        regData.msisdnVerification = {
          msisdn: data.msisdn,
          otp: data.otp,
        };
        if (authenticationMethod === 'MOBILE_ID') {
          delete regData.msisdnVerification;
        }
      }
      if (authenticationMethod === 'BANK' || authenticationMethod === 'ID_CARD'
        || authenticationMethod === 'MOBILE_ID') {
        regData.password = {
          password: `${Math.random().toString(36).substr(2, 19)}1`,
        };
      }
      return {
        types: [
          actionTypes.PUT_CUSTOMER,
          actionTypes.PUT_CUSTOMER_SUCCESS,
          actionTypes.PUT_CUSTOMER_FAIL,
        ],
        data,
        promise: (client) => client.put(`${API_HOST}/customer/registration`, regData),
        afterSuccess: (dispatch, getState) => {
          if (authenticationMethod === AUTH_METHOD_ID_CARD) {
            if (id) {
              dispatch(putMarketingConsents(getState(), data));
            } else {
              const channelData = getChannelDataForMarket(channel);
              dispatch(
                putMarketingConsents(
                  getState(),
                  data,
                  dispatch(createCreditApplication(channelData))
                )
              );
            }
          } else {
            dispatch(putMarketingConsents(getState(), data));
          }
        },
      };
    }

    case 'CUSTOMER_DETAILS': {
      const registrationDetails = {
        documentNumber: data.documentNumber,
        cityOfBirth: data.locality,
        countryOfBirth: data.country,
        citizenship: data.citizenship,
      };

      return {
        types: [
          actionTypes.POST_REGISTRATION_DETAILS,
          actionTypes.POST_REGISTRATION_DETAILS_SUCCESS,
          actionTypes.POST_REGISTRATION_DETAILS_FAIL,
        ],
        data: registrationDetails,
        promise: (client) => client.post(
          `${API_HOST}/customer-cz/registration-details/general`,
          registrationDetails
        ),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
      };
    }

    case 'ADDRESS_CZ': {
      const {
        city, street, postcode, house, door,
      } = data;
      return {
        types: [
          actionTypes.POST_REGISTRATION_DETAILS,
          actionTypes.POST_REGISTRATION_DETAILS_SUCCESS,
          actionTypes.POST_REGISTRATION_DETAILS_FAIL,
        ],
        data: {
          address: {
            city, street, postcode, house, door,
          },
        },
        promise: (client) => client.post(
          `${API_HOST}/customer-cz/registration-details/address`,
          {
            postcode, city, door, street: `${street} ${house}`,
          }
        ),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
      };
    }

    case 'CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION': {
      const bankAccountProvision = data.hasBankAccount;
      return {
        types: [
          actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION,
          actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION_SUCCESS,
          actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/credit-application-mx/clabe-info`,
          { bankAccountProvision },
          { hideError: true }
        ),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
      };
    }

    case 'CUSTOMER_MX_NAMES': {
      return {
        type: actionTypes.PUT_CUSTOMER_MX_NAMES,
        data,
      };
    }

    case 'CUSTOMER_ES_NAMES': {
      return {
        type: actionTypes.PUT_CUSTOMER_ES_NAMES,
        data,
      };
    }

    case 'CUSTOMER_ES_CONTACTS':
    case 'CUSTOMER_MX_ADDRESS':
    case 'CUSTOMER_MX_CONTACTS':
      return putInfoCrediteaAction(data, loan, type);

    case 'INCOME_MX':
      return putInfoCrediteaAction(
        {
          ...data,
          ...educationWorkDetailsCrediteaData(data),
        },
        loan,
        type,
        true
      );

    case 'APPLICATION_QUESTIONS':
      return {
        types: [
          actionTypes.PUT_INFO_CREDITEA,
          actionTypes.PUT_INFO_CREDITEA_SUCCESS,
          actionTypes.PUT_INFO_CREDITEA_FAIL,
        ],
        data: {
          ...data,
          ...educationWorkDetailsCrediteaData(data),
        },
        promise: (client) => new Promise(async (resolve, reject) => {
          try {
            await promiseForPutInfoCrediteaAction(client, data, loan);
            await client.put(
              `${API_HOST}/credit-application/loanpurpose`,
              {
                loanPurpose: data.loanPurpose,
                MMP: toCents(removeNonDigitCharacters(data.mmp)),
                appliedForAmount: toCents(removeNonDigitCharacters(data.appliedForAmount)),
                livingCosts: toCents(removeNonDigitCharacters(data.livingCosts)),
                netIncome: toCents(removeNonDigitCharacters(data.netIncome)),
              }
            );
            resolve();
          } catch (error) {
            reject();
          }
        }),
        afterSuccess: (dispatch, getState) => {
          dispatch(putMarketingConsents(getState(), data));
        },
        afterFailure: (dispatch, getState, response) => {
          if (response.data.errCode === 1267) {
            dispatch(
              change('loan', 'emailInUse', data.email)
            );
          }
        },
      };
    case 'IMPERSONATION_AU': {
      const { authentication } = store.getState();
      const { privacyPolicy } = data;
      const isErrorShownOnFirstStep = (response) => {
        const errorCode = getErrorCode(response);

        return errorCode === 1403 || errorCode === 1490 || errorCode === 1491 || errorCode === 1492;
      };
      const payload = {
        msisdn: authentication.msisdn,
        password: data.newPassword,
        instantMessenger: data.instantMessenger === 'yes',
      };

      return {
        types: [
          actionTypes.SIGNUP_IMPERSONATION_COMPLETE,
          actionTypes.SIGNUP_IMPERSONATION_COMPLETE_SUCCESS,
          actionTypes.SIGNUP_IMPERSONATION_COMPLETE_FAIL,
        ],
        promise: (client) => client.put(
          `${API_HOST}/authentication/user-registration/register-au/register-complete`,
          payload,
          {
            authRequest: true,
            shouldHideErrorForResponse: isErrorShownOnFirstStep,
          }
        ),
        afterSuccess: (dispatch, getState) => {
          const { marketingConsents } = getState();

          // eslint-disable-next-line max-len -- node upgrade
          const nextData = marketingConsents.reduce((acc, { consentName }) => ({
            ...acc,
            [consentName]: true,
          }), { ...data });

          if (privacyPolicy) {
            dispatch(putMarketingConsents(getState(), nextData));
          }

          dispatch(putAdditionalConsents());
        },
      };
    }
    case 'CUSTOMER_BIRTH_DETAILS': {
      return {
        type: actionTypes.PUT_CUSTOMER_BIRTH_DETAILS,
        data,
      };
    }

    case 'CUSTOMER_ES_ADDRESS': {
      return {
        type: actionTypes.PUT_CUSTOMER_ES_ADDRESS,
        data,
      };
    }

    case 'EDUCATION_WORK_CREDITEA': {
      return {
        type: actionTypes.EDUCATION_WORK_CREDITEA,
        data: educationWorkDetailsCrediteaData(data),
      };
    }

    case 'EDUCATION_WORK_MX': {
      const {
        education,
        occupation,
        company,
        employmentDuration,
      } = removeRedundantOccupationInformationMX(data);
      return {
        types: [
          actionTypes.PUT_EDUCATION_WORK_MX,
          actionTypes.PUT_EDUCATION_WORK_MX_SUCCESS,
          actionTypes.PUT_EDUCATION_WORK_MX_FAIL,
        ],
        data: educationWorkDetailsCrediteaData(data),
        promise: (client) => client.put(
          `${API_HOST}/credit-application-mx/education-work`,
          {
            education, occupation, company, employmentDuration,
          }
        ),
        afterSuccess: (dispatch) => dispatch(putScreenName(type)),
      };
    }

    case 'CUSTOMER_LT': {
      const registrationData = {};
      if (data.email) {
        registrationData.email = data.email;
      }
      return {
        types: [
          actionTypes.PUT_CUSTOMER,
          actionTypes.PUT_CUSTOMER_SUCCESS,
          actionTypes.PUT_CUSTOMER_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/customer/registration`,
          registrationData
        ),
      };
    }

    case 'REGISTRIES_LT': {
      return {
        type: actionTypes.REGISTRIES_LT,
      };
    }

    case 'CUSTOMER_LV': {
      const communicationSettings = {
        contractDeliveryMethod: 'EMAIL',
        invoiceDeliveryMethod: 'EMAIL',
      };
      const address = {
        street: ' ',
        postcode: ' ',
        city: ' ',
      };
      const regData = {
        address,
        communicationSettings,
        email: { email: data.email },
        preferredLanguage: getLanguage(),
      };
      return {
        types: [
          actionTypes.PUT_CUSTOMER,
          actionTypes.PUT_CUSTOMER_SUCCESS,
          actionTypes.PUT_CUSTOMER_FAIL,
        ],
        data,
        promise: (client) => {
          const emailReq = client.put(`${API_HOST}/customer/registration`, regData);
          const bankReq = client.put(`${API_HOST}/customer/bank`, { bank: data.bank });
          return Promise.all([emailReq, bankReq]);
        },
        afterSuccess: (dispatch, getState) => {
          dispatch(putMarketingConsents(getState(), data));
        },
      };
    }

    case 'ADDRESS_LT': {
      const { authentication } = store.getState();
      return {
        types: [
          actionTypes.PUT_ADDRESS_INFO,
          actionTypes.PUT_ADDRESS_INFO_SUCCESS,
          actionTypes.PUT_ADDRESS_INFO_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/credit-application-lt/update-personal-info`,
          { livingAddress: data.geoAddress, residence: data.residentialStatus },
          { hideError: true }
        ).then(() => {
          let impersonationRequest = [];
          if (authentication.authenticationMethod === 'IMPERSONATION') {
            const objChannel = { channel: data.channel, shopId: data.shopId };
            if (data.channel === 'Maxima' || data.channel === 'CurrexPrime' || data.channel === 'Perlas') {
              objChannel.shopId = data.shopId;
            }
            impersonationRequest = Promise.all([client.put(
              `${API_HOST}/customer/bankaccount`,
              { bankAccount: data.bankAccount },
              { hideError: true }
            ),
            client.put(
              `${API_HOST}/credit-application/financial-data/channel`,
              objChannel,
              { hideError: true }
            )]);
          }
          return impersonationRequest;
        }),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
      };
    }
    case 'ADDRESS': {
      let address = {
        street: data.street,
        postcode: data.postcode,
        city: data.city,
      };
      let citizenshipData = null;
      let countryOfBirthData = null;
      if (COUNTRY === 'PL') {
        address = {
          postcode: data.postcode1,
          city: data.city1,
          street: `${data.street1} ${data.houseNumber1}`,
          door: data.door1,
          postcode2: data.postcode1,
          city2: data.city1,
          street2: `${data.street1} ${data.houseNumber1}`,
          door2: data.door1,
        };
        if (!data.postAddressAsResidence) {
          address = {
            ...address,
            postcode2: data.postcode2,
            city2: data.city2,
            street2: `${data.street2} ${data.houseNumber2}`,
            door2: data.door2,
          };
        }

        citizenshipData = { firstCitizenship: data.firstCitizenship };
        if (data.hasSecondCitizenship && !isNullOrEmpty(data.secondCitizenship)) {
          citizenshipData.secondCitizenship = data.secondCitizenship;
        }

        countryOfBirthData = { countryOfBirth: data.countryOfBirth };

        store.dispatch(putMarketingConsents(store.getState(), data));
      }
      let URL = `${API_HOST}/customer/address`;
      if (COUNTRY === 'PL') {
        URL = `${API_HOST}/customer-pl/address-step2`;
      }
      return {
        types: [
          actionTypes.PUT_ADDRESS,
          actionTypes.PUT_ADDRESS_SUCCESS,
          actionTypes.PUT_ADDRESS_FAIL,
        ],
        data: { address, citizenshipData, countryOfBirthData },
        promise: (client) => {
          const addressPut = client.put(`${URL}`, address, { hideError: true });
          if (COUNTRY === 'PL') {
            return addressPut.then(() => {
              client.put(
                `${API_HOST}/citizenships/update-citizenships`,
                citizenshipData
              ).then(() => {
                client.put(
                  `${API_HOST}/countryOfBirth`,
                  countryOfBirthData
                );
              });
            });
          }
          return addressPut;
        },
      };
    }

    case 'CUSTOMER_FI': {
      const { bank } = store.getState().authentication;
      let { email } = data;
      let deliveryMethod = 'PAPER';
      if (email) {
        deliveryMethod = 'EMAIL';
        email = { email };
      }
      const communicationSettings = {
        contractDeliveryMethod: deliveryMethod,
        invoiceDeliveryMethod: deliveryMethod,
      };
      let regData = {
        communicationSettings,
        email,
        msisdnVerification: {
          msisdn: data.msisdn,
          otp: data.otp,
        },
        password: {
          password: bank === 'nordea' ? '1234567y' : data.password,
        },
      };
      if (MOBILE_WALLET) {
        regData = {
          communicationSettings,
          email,
        };
      }
      return {
        types: [
          actionTypes.PUT_CUSTOMER,
          actionTypes.PUT_CUSTOMER_SUCCESS,
          actionTypes.PUT_CUSTOMER_FAIL,
        ],
        data,
        promise: (client) => client.put(`${API_HOST}/customer/registration`, regData, { hideError: true }),
        afterSuccess: (dispatch, getState) => {
          dispatch(putMarketingConsents(getState(), data));
        },
        afterFailure: (dispatch, getState, response) => {
          const { data: { errCode } = {} } = response;
          if (errCode === 1404) {
            dispatch(stopSubmit('loan', { otp: `aio.errorCode.${errCode}` }));
            dispatch(change('loan', 'otp', null));
            window.scroll(0, 0);
          }
        },
      };
    }
    case 'INCOME_FI': {
      return {
        type: actionTypes.INCOME,
        data,
      };
    }
    case 'LOAN_TYPE_LT': {
      const url = `${API_HOST}/credit-application/financial-data/loan-type`;
      const loanTypeBody = {
        loanType: data.loanType,
      };
      if (data.loanType === 'FAMILY_LOAN') {
        loanTypeBody.spouseIdentifier = data.spouseIdentifier;
        loanTypeBody.spouseMsisdn = data.spouseMsisdn;
      }

      return {
        types: [
          actionTypes.LOAN_TYPE_LT,
          actionTypes.LOAN_TYPE_LT_SUCCESS,
          actionTypes.LOAN_TYPE_LT_FAIL,
        ],
        data,
        promise: (client) => client.put(
          url,
          loanTypeBody,
          { hideError: true }
        ),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
        afterFailure: (dispatch, getState, response) => {
          if (response.status === 400 && response.data.statusCode === 'VALIDATION') {
            const validationErrors = response.data.params;
            Object.keys(validationErrors).forEach((key) => {
              validationErrors[key] = 'errors.field.required';
            });
            dispatch(
              stopSubmit('loan', validationErrors)
            );
          } else if (response.status === 412) {
            const error = financialDataValidationErrors[response.data.statusCode];
            const validationError = { [error.field]: error.errorMessage };
            dispatch(
              stopSubmit('loan', validationError)
            );
          }
        },
      };
    }
    case 'INCOME_LT': {
      const url = `${API_HOST}/credit-application/financial-data/income`;
      const incomeBody = {
        netIncome: toCents(data.netIncome),
        employmentDuration: data.employmentDuration,
        occupation: data.occupation,
      };
      if (data.loanType === 'FAMILY_LOAN') {
        incomeBody.spouseNetIncome = toCents(data.spouseNetIncome);
      }
      return {
        types: [
          actionTypes.INCOME_LT,
          actionTypes.INCOME_LT_SUCCESS,
          actionTypes.INCOME_LT_FAIL,
        ],
        data,
        promise: (client) => client.put(
          url,
          incomeBody,
          { hideError: true }
        ),
        afterSuccess: (dispatch) => {
          dispatch(putScreenName(type));
        },
        afterFailure: (dispatch, getState, response) => {
          if (response.status === 400 && response.data.statusCode === 'VALIDATION') {
            const validationErrors = response.data.params;
            Object.keys(validationErrors).forEach((key) => {
              validationErrors[key] = 'errors.field.required';
            });
            dispatch(
              stopSubmit('loan', validationErrors)
            );
          }
        },
      };
    }
    case 'INCOME': {
      const { authentication: { mainApplicationId } } = store.getState();

      if (COUNTRY === 'ES' && mainApplicationId) {
        return putLoanPurpose(data, mainApplicationId);
      }
      return {
        type: actionTypes.INCOME,
        data,
      };
    }
    case 'KONTOMATIK': {
      return {
        type: actionTypes.KONTOMATIK,
        data,
      };
    }
    case 'LEGAL': {
      return {
        type: actionTypes.LEGAL,
        data,
      };
    }
    case 'PL_INCOME':
      return {
        type: actionTypes.PL_INCOME,
        data,
      };

    case 'LIABILITIES_LT': {
      const permissionsUrl = `${API_HOST}/credit-application-lt/update-permissions`;
      const permissionsBody = {
        customerFlowStep: 'FINANCIAL_DATA',
        refinanceRequest: data.refinanceRequest,
      };

      const liabilitiesUrl = `${API_HOST}/credit-application/financial-data/obligations`;
      const liabilitiesBody = {
        monthlyObligations: toCents(data.repayments) || 0,
      };
      if (loan.isClientMarried) {
        liabilitiesBody.spouseObligations = toCents(data.spouseObligations) || 0;
      }

      return {
        types: [
          actionTypes.LIABILITIES_LT,
          actionTypes.LIABILITIES_LT_SUCCESS,
          actionTypes.LIABILITIES_LT_FAIL,
        ],
        data,
        promise: (client) => client.put(liabilitiesUrl, liabilitiesBody, { hideError: true })
          .then(() => client.put(permissionsUrl, permissionsBody, { hideError: true })),
        afterSuccess: (dispatch, getState) => {
          const {
            authentication: { id: customerId, isOldCustomer },
            loan: { id: applicationId },
          } = getState();

          dispatch(track('checkout', {
            analyticsLT: () => ({
              page: 'scoring',
              offerSelected: parseFloat(sessionStorage.getItem('offerSelected')),
              userID: customerId,
              applicationID: applicationId,
              clientType: isOldCustomer ? 'Returning Client' : 'New Client',
            }),
          }));
        },
      };
    }

    case 'EXPENSES': {
      return {
        type: actionTypes.EXPENSES,
        data,
      };
    }
    case 'LOAN_EXPENSES': {
      const loans = [];
      if (data.totalMonthlyObligations > 0) {
        loans.push('OTHER');
      }
      return {
        type: actionTypes.LOAN_EXPENSES,
        data: { loans, totalMonthlyObligations: toCents(data.totalMonthlyObligations) },
      };
    }
    case 'INSTANTOR': {
      return {
        type: actionTypes.INSTANTOR,
        data,
      };
    }
    case 'VERIFY': {
      return {
        type: actionTypes.VERIFY,
        data,
      };
    }
    case 'VAS_CONFIRM': {
      const additionalConsents = [
        ...(loan.medicare && loan.medicare.selected ? loan.medicare.consents : []),
        ...(loan.billProtector && loan.billProtector.selected ? loan.billProtector.consents : []),
      ].map(
        ({ consentType }) => ({
          consentType,
          name: consentType,
          value: !!data[consentType],
          revocable: false,
        })
      );
      return {
        types: [
          actionTypes.VAS_CONFIRM,
          actionTypes.VAS_CONFIRM_SUCCESS,
          actionTypes.VAS_CONFIRM_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/customer/additional-consent`,
          additionalConsents,
          { hideError: true }
        ),
      };
    }
    case 'PL_VERIFY': {
      return {
        type: actionTypes.PL_VERIFY,
        data,
        meta: {
          analyticsPL: (storeState) => (
            getAnalyticsObject('thank_you', storeState)
          ),
        },
      };
    }
    case 'CALMA':
      return {
        types: [
          actionTypes.PUT_CALMA,
          actionTypes.PUT_CALMA_SUCCESS,
          actionTypes.PUT_CALMA_FAIL,
        ],
        data,
        promise: (client) => {
          const additionalConsents = ['ES_METLIFE_CALMA_ELIGIBLE', 'ES_METLIFE_CALMA_3RDPARTY', 'ES_METLIFE_CALMA_TERMS'].map((name) => ({
            name,
            consentType: name,
            value: !!data[name],
          }));

          return client.post(
            `${API_HOST}/credit-application-es/calma/submit-pin`,
            { otp: data.otpCalma },
            { hideError: true }
          ).then(() =>
            client.put(
              `${API_HOST}/customer/additional-consent`,
              additionalConsents
            ).then(() =>
              client.put(
                `${API_HOST}/credit-application/extra-services/CALMA`,
                { enabled: true }
              )));
        },
        afterFailure: (dispatch, getState, response) => {
          const { request: { responseURL } = {} } = response;
          const isSubmitPinFailure = responseURL.includes('submit-pin');

          if (isSubmitPinFailure) {
            dispatch(stopSubmit('loan', {
              otpCalma: 'errors.field.otp.invalid',
            }));
          }
        },
        meta: {},
      };

    case 'ONE_CENT': {
      return {
        type: actionTypes.ONE_CENT,
        data,
        meta: {
          analyticsLV: (storeState) => (
            getAnalyticsObject('thankYou', storeState)
          ),
        },
      };
    }
    case 'LOANS': {
      const otherDebts = [];
      const debtTypesExperienced = [];
      loansArray.forEach((item) => {
        const chosenType = data[item.type];
        if (chosenType === 'have') {
          const debt = { type: item.type };
          if (data[`${item.type}_issuerKey`]) {
            debt.issuerKey = data[`${item.type}_issuerKey`];
          }
          if (data[`${item.type}_limit`]) {
            debt.limit = toCents(removeNonDigitCharacters(data[`${item.type}_limit`]));
          }
          if (data[`${item.type}_monthlyPayment`]) {
            debt.monthlyPayment = toCents(removeNonDigitCharacters(data[`${item.type}_monthlyPayment`]));
          }
          if (data[`${item.type}_lastPaymentDate`]) {
            debt.lastPaymentDate = data[`${item.type}_lastPaymentDate`];
          }
          otherDebts.push(debt);
        }
        if (chosenType === 'had' || chosenType === 'have') {
          debtTypesExperienced.push(item.type);
        }
      });
      return {
        type: actionTypes.LOANS,
        data: { otherDebts, debtTypesExperienced },
      };
    }

    case 'LOAN_PURPOSE': {
      const loanPurposes = Object.keys(data.loanPurposes).filter(
        (id) => (data.loanPurposes[id] === true)
      ).map(
        (id) => parseInt(id, 10)
      );
      return {
        types: [
          actionTypes.VERIFY_LOAN_PURPOSES,
          actionTypes.VERIFY_LOAN_PURPOSES_SUCCESS,
          actionTypes.VERIFY_LOAN_PURPOSES_FAIL,
        ],
        data: { loanPurposes },
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/loan-purposes/verify`,
          { loanPurposes }
        ),
      };
    }

    case 'LOAN_PURPOSE_INFO': {
      return {
        types: [
          actionTypes.POST_LOAN_PURPOSES,
          actionTypes.POST_LOAN_PURPOSES_SUCCESS,
          actionTypes.POST_LOAN_PURPOSES_FAIL,
        ],
        data,
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/loan-purposes`,
          {
            loanPurposes: loan.selectedLoanPurposes,
            additionalInfo: data.loanPurposeAdditionalInfo,
          }
        ),
      };
    }

    case 'LOAN_QUESTIONS': {
      const getAmount = (amount) => {
        if (!amount) {
          return null;
        }
        const { rescoringToDoForOpenCreditApplication } = loan;
        return rescoringToDoForOpenCreditApplication ? fromCents(amount) : amount;
      };
      const putLoanPurposeData = {
        ...data,
        appliedForAmount: getAmount(data.appliedForAmount),
        mmp: getAmount(data.mmp),
        livingCosts: getAmount(data.livingCosts),
        netIncome: getAmount(data.netIncome),
      };
      if (COUNTRY === 'ES') {
        return putLoanPurpose(putLoanPurposeData, null);
      }
      return {
        type: actionTypes.LOAN_QUESTIONS,
        data,
      };
    }

    case 'MARITAL_STATUS': {
      return {
        type: actionTypes.MARITAL_STATUS,
        data,
      };
    }

    case 'MARITAL_STATUS_PL': {
      const { maritalStatus, personalIDNumber } = data;
      return {
        types: [
          actionTypes.PUT_MARITAL_STATUS_PL,
          actionTypes.PUT_MARITAL_STATUS_PL_SUCCESS,
          actionTypes.PUT_MARITAL_STATUS_PL_FAIL,
        ],
        data,
        promise: (client) => {
          const requests = [];
          requests.push(client.put(
            `${API_HOST}/credit-application-pl/financialdata-step2`,
            { maritalStatus, personalIDNumber }
          ));
          if (loan.extraServiceQuestions && loan.extraServiceQuestions.length > 0) {
            const extraServiceAnswers = loan.extraServiceQuestions.reduce((answers, question) => ({
              ...answers,
              [question]: data.extraServiceQuestions === 'yes' ? 'YES' : 'NO',
            }), {});
            requests.push(client.put(
              `${API_HOST}/credit-application/extra-service-answers`,
              { extraServiceAnswers }
            ));
          }
          return Promise.all(requests);
        },
      };
    }

    case 'FAMILY_CREDITEA': {
      if (COUNTRY === 'ES') {
        const {
          education,
          employmentDuration,
          householdChildren,
          industry,
          maritalStatus,
          occupation,
          occupationType,
        } = data;
        return {
          types: [
            actionTypes.PUT_FAMILY_CREDITEA,
            actionTypes.PUT_FAMILY_CREDITEA_SUCCESS,
            actionTypes.PUT_FAMILY_CREDITEA_FAIL,
          ],
          data,
          promise: (client) => {
            const keys = {
              education,
              employmentDuration,
              householdChildren,
              industry,
              maritalStatus,
              occupation,
              occupationType,
            };
            const { authentication: { isApplyGuarantor, mainApplicationId } } = store.getState();
            const isPersonificationWithGuarantor = isApplyGuarantor && !!mainApplicationId;
            const newScoringUrl = `${API_HOST}/credit-application-es/scoring`;
            const getScoringStatusUrl = (scoringId) => `${newScoringUrl}/${scoringId}`;
            let scoringId;
            if (isPersonificationWithGuarantor) {
              return client
                .post(newScoringUrl, {})
                .then((response) => {
                  scoringId = response.data.id;
                  return client.put(getScoringStatusUrl(scoringId), keys);
                }).then((response) => ({
                  ...response,
                  data: {
                    ...response.data,
                    scoringId,
                  },
                }));
            }
            return client.put(getScoringStatusUrl(loan.scoringId), keys);
          },
        };
      }
      return {
        type: actionTypes.FAMILY_CREDITEA,
        data,
      };
    }

    case 'FAMILY_MX': {
      const { maritalStatus, loanPurpose } = data;
      return {
        types: [
          actionTypes.PUT_FAMILY_MX,
          actionTypes.PUT_FAMILY_MX_SUCCESS,
          actionTypes.PUT_FAMILY_MX_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/credit-application-mx/family`,
          { maritalStatus, loanPurpose }
        ),
        afterSuccess: (dispatch) => dispatch(putScreenName(type)),
      };
    }
    case 'EDUCATION': {
      return {
        type: actionTypes.EDUCATION,
        data,
      };
    }
    case 'NATIONALITY_LT': {
      const {
        nationality,
        countryOfBirth,
      } = data;

      const originData = {
        nationality,
        countryOfBirth,
      };

      return {
        types: [
          actionTypes.NATIONALITY_LT,
          actionTypes.NATIONALITY_LT_SUCCESS,
          actionTypes.NATIONALITY_LT_FAIL,
        ],
        data,
        promise: (client) => client.put(`${API_HOST}/customer-lt/origin`, originData, { hideError: true }).then(() => client.get(
          `${API_HOST}/credit-application-lt/checkstate`,
          { hideError: true }
        )),
        afterSuccess: (dispatch, getState, response) => {
          dispatch(putScreenName(type));

          if (response.data.state === 'REJECTED') {
            dispatch(track('rejected', {
              analyticsLT: () => {
                const {
                  authentication: { id: authenticationId, isOldCustomer },
                  loan: { id: loanId },
                } = getState();
                return {
                  page: 'nationality',
                  userID: authenticationId,
                  applicationID: loanId,
                  offerSelected: parseFloat(sessionStorage.getItem('offerSelected')),
                  clientType: isOldCustomer ? 'Returning Client' : 'New Client',
                };
              },
            }));
          }
        },
      };
    }
    case 'WORK_STATUS': {
      let workDetails;
      const itemIndex = findIndex(workingStateTypes, (item) =>
        item.type === data.occupation);
      if (workingStateTypes[itemIndex].isDetailsNeeded) {
        workDetails = {
          occupation: data.occupation,
          occupationType: data.occupationType,
          employmentDuration: data.employmentDuration,
          company: data.company,
        };
      } else {
        workDetails = {
          occupation: data.occupation,
        };
      }
      if (COUNTRY === 'EE') {
        workDetails.activityType = data.occupationSector;
      }
      return {
        type: actionTypes.WORK_STATUS,
        data: { workDetails },
      };
    }
    case 'RESIDENCE': {
      return {
        type: actionTypes.RESIDENCE,
        data,
      };
    }
    case 'HOUSEHOLD_CHILDREN': {
      return {
        type: actionTypes.HOUSEHOLD_CHILDREN,
        data,
      };
    }
    case 'OBLIGATIONS': {
      return {
        type: actionTypes.OBLIGATIONS,
        data,
      };
    }
    case 'SCORING': {
      let dataToScoring = {};
      if (PARTNER_PORTAL) {
        const {
          education,
          maritalStatus,
          occupation,
        } = data;

        dataToScoring = {
          education,
          maritalStatus,
          occupation,
        };
      }
      return {
        types: [
          actionTypes.PUT_FINANCIAL,
          actionTypes.PUT_FINANCIAL_SUCCESS,
          actionTypes.PUT_FINANCIAL_FAIL,
        ],
        data,
        promise: (client) => client.post(
          `${API_HOST}/credit-application-es/scoring`,
          dataToScoring,
          {
            hideError: true,
            preventLogout: true,
            headers: { 'Nordigen-Before-Product-Offer': KONTOMATIK_AFTER_INCOME },
          }
        ),
        afterFailure: (dispatch, getState) => {
          if (EMBEDDED) {
            if (loan.rejectionRedirectUrl) {
              setTimeout(
                () => {
                  window.top.location.href = loan.rejectionRedirectUrl;
                },
                2000
              );
            }
          }
          affiliateRequestCustomerRejected(dispatch, getState);
        },
      };
    }

    case 'FINANCIAL_DATA': {
      let cleanData = data;
      let URL = `${API_HOST}/credit-application/financialdata`;

      if (COUNTRY === 'MX') {
        URL = `${API_HOST}/credit-application-mx/financialdata-mx`;

        cleanData = removeRedundantOccupationInformationMX(data);
        const { authentication } = store.getState();
        cleanData.policy = authentication.policy;
      }
      if (COUNTRY === 'LT') {
        URL = `${API_HOST}/credit-application/financial-data/submit`;
      }

      const countriesToApplyRule = ['PL', 'MX', 'AU', 'LT', 'LV', 'EE'];

      if (countriesToApplyRule.includes(COUNTRY)) {
        const { loan: { requestedFinancialData } } = store.getState();
        if (requestedFinancialData) {
          const { amount, maturity } = requestedFinancialData;
          cleanData.requestedPrincipal = amount;
          if ((COUNTRY === 'EE' || COUNTRY === 'PL') && maturity) {
            cleanData.requestedMaturityPeriods = maturity;
          }
        }
      }

      const rootState = store.getState();

      const { loan: newLoan } = rootState;

      const dataToPromise = {
        cleanData,
        financialData: newLoan.financialData,
      };

      return {
        types: [
          actionTypes.PUT_FINANCIAL,
          actionTypes.PUT_FINANCIAL_SUCCESS,
          actionTypes.PUT_FINANCIAL_FAIL,
        ],
        dataToPromise,
        promise: (client) => {
          const config = { hideError: true, preventLogout: true };

          if (COUNTRY === 'LT') {
            const requestedPrincipalUrl = `${API_HOST}/credit-application/financial-data/requested-principal`;
            return new Promise(async (resolve, reject) => {
              const financialDataResponse = await client.put(
                requestedPrincipalUrl,
                { requestedPrincipal: cleanData.requestedPrincipal },
                config
              )
                .catch((error) => reject(error));
              await client.put(URL, null, config)
                .catch((error) => reject(error));

              let amountOfDone = 0;

              let interval;
              let timePassedInSeconds = 0;

              const amountOfSecondsUntilCancel = 30;

              const doRequestForExternalDataStatus = () => {
                if (timePassedInSeconds === 0) {
                  interval = setInterval(() => {
                    timePassedInSeconds += 1;

                    if (timePassedInSeconds >= amountOfSecondsUntilCancel) {
                      clearInterval(interval);
                      resolve(financialDataResponse);
                    }
                  }, 1000);
                }

                setTimeout(async () => {
                  const response = await client.get(`${API_HOST}/credit-application-lt/external-data-status`)
                    .catch(() => resolve(financialDataResponse));

                  response.data.externalDataStatus.forEach((item) => {
                    if (item.status === 'DONE') {
                      amountOfDone += 1;
                    }
                  });

                  const doesRequirementsFullfilled = () => {
                    const conditionalForFamilyLoan = dataToPromise.financialData
                      && dataToPromise.financialData.loanType === 'FAMILY_LOAN'
                      && amountOfDone === 2;

                    const conditionalForPersonalLoan = (dataToPromise.financialData
                        && dataToPromise.financialData.loanType !== 'FAMILY_LOAN'
                        && amountOfDone === 1)
                      || (dataToPromise.financialData
                        // eslint-disable-next-line max-len
                        && dataToPromise.financialData.loanType === undefined
                        && amountOfDone === 1
                      );

                    if (conditionalForFamilyLoan
                      || conditionalForPersonalLoan
                      || timePassedInSeconds >= amountOfSecondsUntilCancel) {
                      return true;
                    }
                    return false;
                  };

                  if (doesRequirementsFullfilled()) {
                    clearInterval(interval);
                    resolve(financialDataResponse);
                  } else {
                    amountOfDone = 0;
                    doRequestForExternalDataStatus();
                  }
                }, 500);
              };

              doRequestForExternalDataStatus();
            });
          }

          return client.put(URL, dataToPromise.cleanData, config);
        },
        afterFailure: (dispatch, getState, response) => {
          if (EMBEDDED && loan.rejectionRedirectUrl) {
            setTimeout(() => {
              window.top.location.href = loan.rejectionRedirectUrl;
            }, 2000);
          } else if (COUNTRY === 'FI' && !MOBILE_WALLET && !EMBEDDED) {
            setTimeout(() => {
              window.location.href = 'http://info.credit24.com/fi/vaihtoehto/';
            }, 3000);
          }
          affiliateRequestCustomerRejected(dispatch, getState);
          if (COUNTRY === 'MX') {
            const { data: { message } } = response;

            const isRedirectionMessage = message === CREDIT_REJECTION_EXT_SCORE_CUT
              || message === CREDIT_REJECTION_BDC_SCORING_NEGATIVE;

            // If rejection one of above we go to PROVIDENT_CHECK
            if (isRedirectionMessage) {
              // Go to provident scoring
              dispatch(providentScoringMX(message));
            } else {
              resetIsFilledByBroker(getState);
              dispatch(saveLoanForm());
            }
          }
          if (COUNTRY === 'LT') {
            if (response.status === 412) {
              if (!response.data.params.rejectReason) {
                const error = financialDataValidationErrors[response.data.statusCode];
                const validationError = { [error.field]: error.errorMessage };
                dispatch(
                  stopSubmit('loan', validationError)
                );
              }

              dispatch(track('rejected', {
                analyticsLT: () => {
                  const {
                    authentication: { id: authenticationId, isOldCustomer },
                    loan: { id: loanId },
                  } = getState();
                  return {
                    page: 'scoring',
                    userID: authenticationId,
                    applicationID: loanId,
                    offerSelected: parseFloat(sessionStorage.getItem('offerSelected')),
                    clientType: isOldCustomer ? 'Returning Client' : 'New Client',
                  };
                },
              }));
            }
          }
        },
        afterSuccess: (dispatch, getState) => {
          const { loan: { isFilledByBrokerPayLater } } = getState();
          if (COUNTRY === 'MX' && !isFilledByBrokerPayLater) {
            resetIsFilledByBroker(getState);
            dispatch(putScreenName('INCOME_MX'));
          }
          if (COUNTRY === 'CZ') {
            dispatch(putScreenName('OBLIGATIONS'));
          }
          if (COUNTRY === 'LT') {
            removeCachedQueryString();
            dispatch(putScreenName('LIABILITIES_LT'));
          }
        },
        meta: {
          analyticsLV: (storeState) => (
            getAnalyticsObject('7', storeState)
          ),
          analyticsFI: (storeState) => (
            getAnalyticsObject('7', storeState)
          ),
          analyticsPL: (storeState) => (
            getAnalyticsObject('7', storeState)
          ),
          analyticsEE: (storeState) => (
            getAnalyticsObject('7', storeState, cleanData)
          ),
          analyticsMX: (storeState) => (
            getAnalyticsObject('6', storeState, cleanData)
          ),
        },
      };
    }

    case 'BANK_ACCOUNT_PL':
    case 'BANK_ACCOUNT': {
      let url = `${API_HOST}/customer/bankaccount`;
      if (COUNTRY === 'MX') {
        const suffix = data.bankAccountType !== 'clabe' ? 'payment-card' : 'bankaccount';
        url = `${API_HOST}/customer-mx/${suffix}`;
      } else if (COUNTRY === 'PL') {
        url = `${API_HOST}/customer-pl/bankaccount`;
      }
      const {
        bankAccount, bankCode, bankAccountDuration, bankAccountDebitCard,
      } = data;
      const bank = {};
      if (COUNTRY === 'MX' && data.bankAccountType !== 'clabe') {
        bank.bankCode = bankCode;
        bank.cardNumber = bankAccountDebitCard;
      } else {
        bank.bankAccount = bankAccount;
      }
      return {
        types: [
          actionTypes.PUT_BANK_ACCOUNT,
          actionTypes.PUT_BANK_ACCOUNT_SUCCESS,
          actionTypes.PUT_BANK_ACCOUNT_FAIL,
        ],
        data: {
          ...bank,
          bankAccountDuration,
        },
        promise: (client) => {
          const bankAccountPut = client.put(url, bank, { hideError: true });
          if (COUNTRY === 'PL') {
            const additionalConsents = ['DELTA_VISTA_PROCESSING', 'DELTA_VISTA_VERIFICATION'].map((name) => ({
              name,
              consentType: `CREDIT_RISK_${name}`,
              value: !!data.creditReliabilityVerification,
            }));

            return Promise.all([
              bankAccountPut.then(() => client.put(
                `${API_HOST}/customer-pl/communication-settings-pl`,
                {
                  customerFlowStep: 'FINANCIAL_DATA',
                  powerOfAttorney: true,
                  termsOfServiceViaWebsite: true,
                }
              )),
              client.put(`${API_HOST}/customer/additional-consent`, additionalConsents),
            ]);
          }
          return bankAccountPut;
        },
        afterSuccess: (dispatch) => {
          if (COUNTRY === 'MX') {
            dispatch(putScreenName(type));
          }
        },
        afterFailure: (dispatch, getState, response) => {
          if (COUNTRY === 'PL') {
            if (response.data.statusCode === 'VALIDATION') {
              dispatch(
                stopSubmit('loan', { bankAccount: 'errors.field.iban' })
              );
            }
          }
          if (COUNTRY === 'MX') {
            if (data.bankAccountType !== 'clabe' && response.data.errCode === 2816) {
              dispatch(
                change('loan', 'bankAccountDebitCardInUse', bankAccountDebitCard)
              );
            } else if (response.data.statusCode === 'VALIDATION') {
              dispatch(
                change('loan', 'invalidCreditCardNumber', bankAccountDebitCard)
              );
            }
          }
        },
      };
    }

    case 'PAYMENT_DAY': {
      const obj = { dueDate: data.paymentDay };
      if (data.paymentDay === loan.loanDueDateRange.selectedDate && COUNTRY !== 'FI') {
        const paymentDayAction = {
          type: actionTypes.PUT_PAYMENT_DAY_SUCCESS,
          data: obj,
        };
        if (COUNTRY === 'ES') {
          return {
            ...loanPromise(data.firstDrawAmount, loan.selectedProduct),
            afterSuccess: (dispatch, getState) => {
              dispatch(paymentDayAction);
              affiliateRequestContractSubmitted(dispatch, getState);
            },
          };
        }
        return paymentDayAction;
      }

      return {
        types: [
          actionTypes.PUT_PAYMENT_DAY,
          actionTypes.PUT_PAYMENT_DAY_SUCCESS,
          actionTypes.PUT_PAYMENT_DAY_FAIL,
        ],
        promise: (client) => {
          let dueDateUrl = `${API_HOST}/credit-application/duedate`;
          const requests = [];
          if (data.paymentDay === loan.loanDueDateRange.defaultDate) {
            requests.push(client.delete(dueDateUrl, { isPaymentDateRequest: true }));
          } else if (data.paymentDay !== loan.loanDueDateRange.selectedDate) {
            if (COUNTRY === 'PL' && loan.isFilledByBroker) {
              dueDateUrl = `${API_HOST}/hybrid/application/${loan.id}/selectDueDate`;
            }
            requests.push(client.put(dueDateUrl, obj, { isPaymentDateRequest: true }));
          }
          if (COUNTRY === 'FI') {
            const communicationSettings = {
              invoiceDeliveryMethod: data.documentsDeliveryMethod,
              contractDeliveryMethod: data.documentsDeliveryMethod,
            };
            requests.push(client.put(`${API_HOST}/customer/communication-settings`, communicationSettings));
          }
          if (requests.length) {
            return Promise.all(requests);
          }
          return Promise.resolve();
        },
        afterSuccess: (dispatch) => {
          if (COUNTRY === 'PL') {
            dispatch(getPaymentPlan());
          }
          if (COUNTRY === 'ES') {
            dispatch(getPaymentPlanThenLoanPromise(data.firstDrawAmount, loan.selectedProduct));
          }
          if (COUNTRY === 'LT') {
            dispatch(getDueDateRange());
          }
        },
        meta: {
          analyticsFI: (storeState) => (
            getAnalyticsObject('9', storeState)
          ),
        },
      };
    }
    case 'CONTRACT_DETAILS': {
      const paymentDayObj = { dueDate: data.paymentDay };
      const dueDateUrl = `${API_HOST}/credit-application/duedate`;
      const bankAccountUrl = `${API_HOST}/customer/bankaccount`;
      const { bankAccount, bankAccountDuration } = data;
      const bankAccountObj = { bankAccount };

      return {
        types: [
          actionTypes.PUT_CONTRACT_DETAILS,
          actionTypes.PUT_CONTRACT_DETAILS_SUCCESS,
          actionTypes.PUT_CONTRACT_DETAILS_FAIL,
        ],
        data: {
          ...bankAccountObj,
          bankAccountDuration,
        },
        promise: (client) => {
          const requests = [];
          if (data.paymentDay === loan.loanDueDateRange.defaultDate) {
            requests.push(client.delete(dueDateUrl, { isPaymentDateRequest: true }));
          } else if (data.paymentDay !== loan.loanDueDateRange.selectedDate) {
            requests.push(client.put(dueDateUrl, paymentDayObj, { isPaymentDateRequest: true }));
          }
          requests.push(client.put(bankAccountUrl, bankAccountObj, { hideError: true }));

          return Promise.all(requests);
        },
      };
    }
    case 'POST_OFFICE': {
      const office = {
        authenticationPostOfficeId: data.authenticationPostOfficeId,
      };
      return {
        types: [
          actionTypes.PUT_POST_OFFICE,
          actionTypes.PUT_POST_OFFICE_SUCCESS,
          actionTypes.PUT_POST_OFFICE_FAIL,
        ],
        promise: (client) => client.put(`${API_HOST}/credit-application/authpostoffice`, office),
      };
    }
    case 'GUARANTOR':
    case 'PRODUCT':
    case 'CONFIRM_ZERO_DRAW':
    case 'CREDIT_LINE': {
      let URL = `${API_HOST}/credit-application/product`;
      if (COUNTRY === 'AU') {
        return putProductActionAU(data, loan);
      }
      if (COUNTRY === 'MX') {
        URL = `${API_HOST}/credit-application-mx/product`;
      }
      let storeState;
      const obj = {
        id: loan.selectedProduct.id,
        firstDrawAmount: toCents(data.firstDrawAmount) || 0,
      };
      let montlyPaymentPayload = {};
      const urlMontlyPayment = `${API_HOST}/credit-application/meta-data/monthly-payment`;
      if (COUNTRY === 'CZ') {
        obj.firstDrawAmount = loan.availableSumsIndex[data.loanAmountIndex] || 0;
        montlyPaymentPayload = {
          monthlyPaymentInCents: data.monthlyPaymentInCents,
          paymentPeriodInMonths: data.selectedMaturity ? data.selectedMaturity : 1,
        };
      }
      if (['LV', 'ES', 'PL', 'LT'].includes(COUNTRY)) {
        storeState = store.getState();
        if (isInstallment(loan.productType)) {
          const principal = storeState.loan.availablePrincipalsIndex[data.loanAmountIndex];
          const selectedProduct = find(storeState.loan.availableProducts, {
            principal,
            maturityPeriods: parseInt(data.selectedMaturity, 10),
          });

          obj.id = selectedProduct.id;
          obj.firstDrawAmount = principal;
        } else if (COUNTRY === 'LV' && loan.availablePrincipalsIndex.length > 0) {
          obj.firstDrawAmount = loan.availablePrincipalsIndex[data.firstDrawAmount];
        }
      }
      if ((COUNTRY === 'FI' || COUNTRY === 'LV') && loan.isFilledByBroker) {
        URL = `${API_HOST}/credit-application/updateDrawAmount`;
        delete obj.id;
      }
      if (COUNTRY === 'EE') {
        if (type === 'CREDIT_LINE') {
          const creditLine = loan.availablePrincipalsIndex[data.creditLine];
          obj.id = loan.productsByPrincipals[creditLine].id;
        } else {
          const valueInAutoApprovedLimit = loan.appPassedAutoApproveConditions
            && obj.firstDrawAmount <= loan.maxAutoApprovalProduct.principal;
          const isMaxAmountChosen = obj.firstDrawAmount === loan.product.firstDrawAmount;
          if (valueInAutoApprovedLimit) {
            const creditLineIndex = loan.availablePrincipalsIndex
              // eslint-disable-next-line max-len
              .findIndex((principal) => principal >= loan.maxAutoApprovalProduct.principal);
            store.dispatch(change('loan', 'creditLine', creditLineIndex));
            obj.id = loan.maxAutoApprovalProduct.id;
          } else if (isMaxAmountChosen) {
            store.dispatch(change('loan', 'creditLine', loan.availablePrincipalsIndex.length - 1));
          } else {
            store.dispatch(change('loan', 'creditLine', null));
            return {
              type: actionTypes.STEP_FORWARD,
            };
          }
        }
      }
      if (type === 'PRODUCT' && COUNTRY === 'MX' && data.firstDrawAmount === 0) {
        store.dispatch(putScreenName(type));
        return {
          type: actionTypes.STEP_FORWARD,
        };
      }
      const actionData = {
        firstDrawAmount: toCents(data.firstDrawAmount) || 0,
      };
      if (COUNTRY === 'ES') {
        obj.firstDrawAmount = loan.selectedProduct.firstDrawAmount;
        const { authentication } = store.getState();

        if (type === 'GUARANTOR' && !authentication.isSelfServiceImpersonation) {
          if (!loan.isApplicationWithProduct) {
            return putProductES(data, loan.financialData.guarantorType, loan.id);
          }

          return patchGuarantorThenGetFees(data, loan.financialData.guarantorType, loan.id);
        }
        if (type === 'PRODUCT' && authentication.isSelfServiceImpersonation) {
          return putMigrationProductES(data, loan.financialData.guarantorType, loan.id);
        }
        return {
          type: actionTypes.STEP_FORWARD,
        };
      }

      if (COUNTRY === 'PL' && loan.isFilledByBroker) {
        URL = `${API_HOST}/hybrid/application/${loan.id}/products`;
        return {
          types: [
            actionTypes.PUT_HYBRID_PRODUCT,
            actionTypes.PUT_HYBRID_PRODUCT_SUCCESS,
            actionTypes.PUT_HYBRID_PRODUCT_FAIL,
          ],
          data: obj,
          promise: (client) => client
            .put(URL, { selectedProduct: obj.id })
            .then(() => client.get(URL)),
        };
      }

      if (COUNTRY === 'LV' && VARIANT_B) {
        if (loan.canChangeLimit) {
          const creditLine = loan.availablePrincipalsIndex[data.creditLimit];
          obj.id = loan.productsByPrincipals[creditLine].id;
          delete obj.firstDrawAmount;
          actionData.firstDrawAmount = loan.selectedProduct.firstDrawAmount;
          actionData.selectedMaturity = loan.selectedProduct.selectedMaturity;
          actionData.principal = creditLine;
        } else {
          actionData.selectedMaturity = data.selectedMaturity;
        }
      }

      return {
        types: [
          actionTypes.PUT_PRODUCT,
          actionTypes.PUT_PRODUCT_SUCCESS,
          actionTypes.PUT_PRODUCT_FAIL,
        ],
        data: actionData,
        promise: (client) => {
          if (COUNTRY === 'CZ') {
            return client.put(urlMontlyPayment, montlyPaymentPayload)
              .then(() => client.put(
                URL,
                {
                  ...obj,
                  id: loan.selectedProduct.id,
                }
              ));
          }
          return client.put(URL, obj);
        },
        afterSuccess: (dispatch, getState) => {
          if ((COUNTRY === 'FI' || COUNTRY === 'LV' || COUNTRY === 'ES') && loan.isFilledByBroker) {
            dispatch(putMarketingConsents(getState(), data));
          }
          if (COUNTRY === 'CZ') {
            dispatch(track('checkout', {
              analyticsCZ: () => {
                const {
                  authentication: { id: authenticationId, isOldCustomer },
                  loan: { id: loanId },
                } = store.getState();
                return {
                  event: 'checkout',
                  page: 'psychometric',
                  userID: authenticationId,
                  applicationID: loanId,
                  clientType: isOldCustomer ? 'Returning Client' : 'New Client',
                };
              },
            }));
          }
          if (COUNTRY === 'MX' || COUNTRY === 'CZ' || COUNTRY === 'LT') {
            dispatch(putScreenName(type));
          }
          if (COUNTRY === 'PL') {
            const shouldPutBillProtector = loan.billProtector && loan.billProtector.isAvailable;
            if (loan.medicare && loan.medicare.isAvailable) {
              dispatch(
                putMedicare(
                  loan.medicare.selected,
                  () => {
                    if (shouldPutBillProtector) {
                      dispatch(putBillProtector(loan.billProtector.selected));
                    }
                  }
                )
              );
            } else if (shouldPutBillProtector) {
              dispatch(putBillProtector(loan.billProtector.selected));
            }
          }
          if (COUNTRY === 'LV') {
            if (loan.repaymentPlan && loan.repaymentPlan.isAvailable && data.repaymentPlan === 'yes') {
              dispatch(putRepaymentPlan());
            }
            dispatch(pushGaStepEvent('8', data));
          }
        },
        meta: {},
      };
    }
    case 'NORDIGEN': {
      const URL = `${API_HOST}/nordigen/link`;
      const obj = {
        bankId: loan.selectedNordigenBank.id,
        applicationId: loan.id,
      };
      return {
        types: [
          actionTypes.POST_NORDIGEN_BANK,
          actionTypes.POST_NORDIGEN_BANK_SUCCESS,
          actionTypes.POST_NORDIGEN_BANK_FAIL,
        ],
        promise: (client) => client.post(URL, obj),
        afterFailure: (dispatch) => {
          dispatch(setNordigenError(true));
          dispatch(skipNordigenStep(false));
        },
        meta: {},
      };
    }
    case 'CREDIT_LIMIT': {
      const URL = `${API_HOST}/credit-application-cz/change-product`;

      const selectedPrincipal = loan.availablePrincipalsIndex[data.loanAmountIndex] || 0;
      const { availableProducts } = loan;
      let { firstDrawAmount } = loan.selectedProduct;

      const productsByPrincipal = availableProducts
        .filter((product) => product.principal === selectedPrincipal);

      const chooseProduct = productsByPrincipal[0];

      const shouldUpdateFirstDrwaAmount = chooseProduct.principal < firstDrawAmount;
      if (shouldUpdateFirstDrwaAmount) {
        firstDrawAmount = chooseProduct.principal;
      }
      chooseProduct.firstDrawAmount = firstDrawAmount;
      chooseProduct.maturitiesToLoad = loan.selectedProduct.maturitiesToLoad;
      const selectedMaturity = data.selectedMaturity ? data.selectedMaturity : 1;

      const monthlyPaymentInCents = toCents(fromCents(chooseProduct.firstDrawAmount) / selectedMaturity, 'float');

      const obj = {
        id: chooseProduct.id,
      };

      return {
        types: [
          actionTypes.PUT_CHANGE_PRODUCT,
          actionTypes.PUT_CHANGE_PRODUCT_SUCCESS,
          actionTypes.PUT_CHANGE_PRODUCT_FAIL,
        ],
        promise: (client) => {
          if (COUNTRY === 'CZ' && shouldUpdateFirstDrwaAmount) {
            const urlMontlyPayment = `${API_HOST}/credit-application/meta-data/monthly-payment`;
            const montlyPaymentPayload = {
              monthlyPaymentInCents,
              paymentPeriodInMonths: data.selectedMaturity ? data.selectedMaturity : 1,
            };

            return client.put(urlMontlyPayment, montlyPaymentPayload)
              .then(() => client.put(URL, obj));
          }
          return client.put(URL, obj);
        },
        afterSuccess: (dispatch) => {
          if (COUNTRY === 'CZ' && shouldUpdateFirstDrwaAmount) {
            dispatch(change('loan', 'monthlyPaymentInCents', monthlyPaymentInCents));
          }
          dispatch(setSelectedProduct(chooseProduct));
          dispatch(getMMPsByMonth(chooseProduct.maturitiesToLoad, chooseProduct.id));
        },
        meta: {},
      };
    }
    case 'SUBMIT_CHANGE_PRODUCT': {
      const { authentication: { confirmationKey } } = store.getState();
      const payloadSubmit = {
        otp: data.otp,
        token: confirmationKey,
      };
      const submitUrl = `${API_HOST}/credit-application-cz/product-change/confirm`;

      if (data.otp) {
        return {
          types: [
            actionTypes.SUBMIT,
            actionTypes.SUBMIT_SUCCESS,
            actionTypes.SUBMIT_FAIL,
          ],
          promise: (client) => client.post(
            submitUrl,
            payloadSubmit,
            { authRequest: true }
          ),
          afterFailure: (dispatch) => {
            dispatch(
              stopSubmit('loan', { otp: 'errors.field.otp.invalid' })
            );
          },
          afterSuccess: (dispatch, getState, response) => {
            dispatch(pushGaPurchase());
            affiliateRequestContractSubmitted(dispatch, getState);
            if (
              MOBILE_WALLET
              && (
                response.data.state === 'APPROVED'
                || !loan.documentRequirements
                || loan.documentRequirements.length === 0
              )
            ) {
              dispatch(mwResumeFlow());
            }
            dispatch(putScreenName(type));
          },
          meta: {
            analyticsAU: (storeState) => getAnalyticsObjectAU('sign_contract', storeState),
          },
        };
      }

      return requestOTPCzChangeProduct();
    }
    case 'SUBMIT': {
      const {
        authentication: { isSelfServiceImpersonation },
      } = store.getState();

      if (isSelfServiceImpersonation) {
        return migrationLoanPromise(loan.selectedProduct);
      }

      const URL = `${API_HOST}/credit-application-au/legal-page`;

      const consent = loan.legalDocument.consents.reduce((result, consentValue) => ({
        ...result,
        [consentValue]: data[`offer_submit_${consentValue}`],
      }), {});

      const dataToSend = { consent };

      return {
        types: [
          actionTypes.SUBMIT,
          actionTypes.SUBMIT_SUCCESS,
          actionTypes.SUBMIT_FAIL,
        ],
        promise: (client) => client.post(URL, dataToSend),
        afterSuccess: (dispatch, getState, response) => {
          affiliateRequestContractSubmitted(dispatch, getState);
          dispatch(getUserSession(true));
          if (
            MOBILE_WALLET
            && (
              response.data.state === 'APPROVED'
              || !loan.documentRequirements
              || loan.documentRequirements.length === 0
            )
          ) {
            dispatch(mwResumeFlow());
          }
        },
        meta: {
          analyticsAU: (storeState) => getAnalyticsObjectAU('sign_contract', storeState),
        },
      };
    }

    case 'CREDENTIALS': {
      return {
        types: [
          actionTypes.PUT_CREDENTIALS,
          actionTypes.PUT_CREDENTIALS_SUCCESS,
          actionTypes.PUT_CREDENTIALS_FAIL,
        ],
        promise: (client) => {
          const requests = [
            client.put(
              `${API_HOST}/hybrid/application/${loan.id}/password`,
              { password: data.password }
            ),
          ];
          if (data.email) {
            requests.push(client.put(`${API_HOST}/customer-pl/email`, { email: data.email }));
          }
          return Promise.all(requests);
        },
      };
    }

    case 'LIBERTO': {
      if (EMBEDDED || PARTNER_PORTAL) {
        return {
          type: actionTypes.LOANSUBMITTED,
        };
      }
      if (loan.libertoSucceeded) {
        return {
          type: actionTypes.STEP_FORWARD,
        };
      }
      return {
        type: actionTypes.SKIP_LIBERTO,
      };
    }
    case 'VERIFICATION_WAY': {
      return {
        type: actionTypes.STEP_FORWARD,
      };
    }

    case 'VERIFICATION':
      return {
        type: actionTypes.VERIFICATION,
      };

    case 'EID_INTRO': {
      return {
        type: actionTypes.EID_INTRO,
        data,
      };
    }

    case 'CUSTOMER_PL_AGREEMENTS': {
      store.dispatch(putMarketingConsents(store.getState(), data));
      return {
        types: [
          actionTypes.PUT_CUSTOMER_PL_AGREEMENTS,
          actionTypes.PUT_CUSTOMER_PL_AGREEMENTS_SUCCESS,
          actionTypes.PUT_CUSTOMER_PL_AGREEMENTS_FAIL,
        ],
        promise: (client) => client.put(
          `${API_HOST}/customer-pl/communication-settings-pl`,
          { customerFlowStep: 'REGISTRATION' }
        ),
      };
    }

    case 'IDENT_DOCUMENTS': {
      const customer = {
        creditApplication: { stepName: 'IDENT_DOCUMENTS' },
      };

      const { identDocumentType } = data;
      if (identDocumentType === DRIVING_LICENSE) {
        customer.drivingLicence = {
          number: data.drivingLicenceNumber,
          state: data.drivingLicenceState,
          expiryDate: [
            parseInt(data.drivingLicenceExpirationYear, 10),
            parseInt(data.drivingLicenceExpirationMonth, 10),
            parseInt(data.drivingLicenceExpirationDay, 10),
          ],
        };
      } else if (identDocumentType === MEDICARE) {
        const year = parseInt(data.medicareExpirationYear, 10);
        const month = parseInt(data.medicareExpirationMonth, 10);

        const cardType = data.cardColour;
        const isGreenCard = cardType === MEDICARE_CARD_COLOR_GREEN;

        const day = isGreenCard
          ? moment(`${year}-${month}`, 'YYYY-M').daysInMonth()
          : parseInt(data.medicareExpirationDay, 10);

        const dateWithoutDay = [
          year,
          month,
          moment(`${year}-${month}`, 'YYYY-M').daysInMonth(),
        ];

        const expiryDate = INCREASE_APP ? dateWithoutDay : [year, month, day];

        customer.medicare = {
          number: removeNonDigitCharacters(data.medicareNumber),
          referenceNumber: parseInt(data.medicareReferenceNumber, 10),
          cardColour: cardType,
          expiryDate,
        };
      } else if (identDocumentType === PASSPORT) {
        const {
          passportNumber: number,
          countryOfIssue,
          passportExpirationYear,
          passportExpirationMonth,
          passportExpirationDay,
        } = data;

        customer.passport = {
          number,
          ...(!INCREASE_APP && {
            countryOfIssue,
            expiryDate: [
              Number(passportExpirationYear),
              Number(passportExpirationMonth),
              Number(passportExpirationDay),
            ],
          }),
        };
      }

      return {
        types: [
          actionTypes.IDENT_DOCUMENTS,
          actionTypes.IDENT_DOCUMENTS_SUCCESS,
          actionTypes.IDENT_DOCUMENTS_FAIL,
        ],
        data: customer,
        promise: (client) =>
          client.post(
            `${API_HOST}/credit-application-au/info`,
            { customer }
          ),
        afterFailure: (dispatch, getState, response) => {
          const { data: { statusCode } } = response;

          if (identDocumentType === MEDICARE && statusCode === 'VALIDATION') {
            dispatch(stopSubmit('loan', {
              medicareNumber: 'errors.field.medicareNumber.format',
            }));
          }
        },
      };
    }

    case 'EDUCATION_WORK': {
      let employer = {
        type: data.occupation,
      };

      const workingStateType = find(workingStateTypes, (item) => (
        item.type === data.occupation
      )) || {};
      if (workingStateType.areDetailsNeeded) {
        employer = {
          ...employer,
          name: data.company,
          jobTitle: data.jobTitle,
          duration: data.employmentDuration,
        };
        if (data.secondJob === 'yes') {
          employer.secondEmployerName = data.secondCompany;
        }
      }

      return {
        types: [
          actionTypes.EDUCATION_WORK,
          actionTypes.EDUCATION_WORK_SUCCESS,
          actionTypes.EDUCATION_WORK_FAIL,
        ],
        data: {
          employer,
          educationLevel: data.education,
        },
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info`,
          {
            customer: {
              creditApplication: {
                ...loan.customer.creditApplication,
                employer,
                educationLevel: data.education,
                stepName: 'EDUCATION_WORK',
              },
            },
          }
        ),
      };
    }

    case 'ADDRESS_AU': {
      const address = {
        residentialStatus: data.residentialStatus,
        streetNumber: data.streetNumber,
        street: data.street,
        state: data.addressState,
        postCode: data.postCode,
        city: data.city,
      };
      if (data.unitNumber) {
        address.unitNumber = data.unitNumber;
      }
      return {
        types: [
          actionTypes.ADDRESS_AU,
          actionTypes.ADDRESS_AU_SUCCESS,
          actionTypes.ADDRESS_AU_FAIL,
        ],
        data: { address },
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info`,
          { customer: { address, creditApplication: { stepName: 'ADDRESS_AU' } } }
        ),
      };
    }

    case 'FAMILY_AU': {
      const customer = {
        gender: data.gender,
        creditApplication: {
          maritalStatus: data.maritalStatus,
          residencyStatus: data.residencyStatus,
          soleEarnerIndicator: data.soleEarnerIndicator === 'yes',
          numberOfDependants: parseInt(data.numberOfDependants, 10),
          stepName: 'FAMILY_AU',
        },
        ...(!INCREASE_APP && { countryOfOrigin: data.countryOfOrigin }),
      };
      return {
        types: [
          actionTypes.FAMILY_AU,
          actionTypes.FAMILY_AU_SUCCESS,
          actionTypes.FAMILY_AU_FAIL,
        ],
        data: customer,
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info`,
          { customer }
        ),
      };
    }

    case 'INCOME_AU': {
      const obj = {
        income: {
          amount: toCents(removeNonDigitCharacters(data.incomeAmount)),
          frequency: data.incomeFrequency,
        },
      };
      if (data.governmentBenefits === 'yes') {
        obj.governmentBenefits = {
          amount: toCents(removeNonDigitCharacters(data.governmentBenefitsAmount)),
          frequency: data.governmentBenefitsFrequency,
        };
      } else {
        obj.governmentBenefits = undefined;
      }
      if (data.incomePayDayDate) {
        const date = moment(data.incomePayDayDate);
        obj.income.nextPayDay = [
          parseInt(date.year(), 10),
          parseInt(date.month(), 10) + 1,
          parseInt(date.date(), 10),
        ];
      }
      return {
        types: [
          actionTypes.INCOME_AU,
          actionTypes.INCOME_AU_SUCCESS,
          actionTypes.INCOME_AU_FAIL,
        ],
        data: obj,
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info`,
          {
            customer: {
              creditApplication: {
                ...loan.customer.creditApplication,
                ...obj,
                stepName: 'INCOME_AU',
              },
            },
          }
        ),
      };
    }

    case 'EXPENSES_AU': {
      const livingExpenses = {
        housingCosts: toCents(removeNonDigitCharacters(data.housingCosts)) || 0,
        housingCostsFrequency: data.housingCostsFrequency,
        foodGroceries: toCents(removeNonDigitCharacters(data.foodGroceriesExpenses)) || 0,
        transport: toCents(removeNonDigitCharacters(data.transportExpenses)) || 0,
        healthMedical: toCents(removeNonDigitCharacters(data.healthMedicalExpenses)) || 0,
        insurance: toCents(removeNonDigitCharacters(data.insuranceExpenses)) || 0,
        utilities: toCents(removeNonDigitCharacters(data.utilitiesExpenses)) || 0,
        phoneInternet: toCents(removeNonDigitCharacters(data.phoneInternetExpenses)) || 0,
        other: toCents(removeNonDigitCharacters(data.otherExpenses)) || 0,
        totalMonthlyLivingExpenses: toCents(
          removeNonDigitCharacters(data.totalLivingExpenses)
        ) || 0,
      };

      return {
        types: [
          actionTypes.EXPENSES_AU,
          actionTypes.EXPENSES_AU_SUCCESS,
          actionTypes.EXPENSES_AU_FAIL,
        ],
        data: { livingExpenses },
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info`,
          {
            customer: {
              creditApplication: {
                ...loan.customer.creditApplication,
                livingExpenses,
                stepName: 'EXPENSES_AU',
              },
            },
          }
        ),
      };
    }

    case 'LOANS_AU': {
      const requestedValues = {};
      const { loan: { requestedFinancialData = {} } } = store.getState();
      const { maturity, amount } = requestedFinancialData;
      const obj = {
        loanPayments: {
          count: parseInt(data.loanPaymentsCount, 10) || 0,
          totalRepayments: toCents(removeNonDigitCharacters(data.totalRepayments)) || 0,
          frequency: data.frequency,
        },
        creditCards: {
          count: parseInt(data.creditCardsCount, 10) || 0,
          totalLimit: toCents(removeNonDigitCharacters(data.totalLimit)) || 0,
        },
        consent: {
          'credit-bureau-enquiries': data.creditBureauEnquiries,
        },
      };

      if (data.smallLoanPayments === 'yes') {
        obj.saccLoans = {
          number: parseInt(data.smallLoanPaymentsCount, 10) || 0,
          amountOfRepayments: toCents(
            removeNonDigitCharacters(data.smallLoanPaymentsTotalRepayments)
          ),
          frequency: data.smallLoanPaymentsFrequency,
        };
      } else {
        obj.saccLoans = undefined;
      }

      if (maturity) {
        requestedValues.requestedMaturityPeriods = maturity;
      }

      if (amount) {
        requestedValues.requestedPrincipal = amount;
      }

      return {
        types: [
          actionTypes.LOANS_AU,
          actionTypes.LOANS_AU_SUCCESS,
          actionTypes.LOANS_AU_FAIL,
        ],
        data: { ...obj },
        promise: (client) => client.post(
          `${API_HOST}/credit-application-au/info-final`,
          {
            customer: {
              creditApplication: {
                ...loan.customer.creditApplication,
                ...obj,
                ...requestedValues,
                stepName: 'LOANS_AU',
              },
            },
          }
        ),
      };
    }

    case 'REFERENCE': {
      const referenceData = {
        name: data.referenceName,
        telephoneNumber: data.referencePhone,
      };
      if (!isNullOrEmpty(data.referenceRelation)) {
        referenceData.relation = data.referenceRelation;
      }
      return {
        types: [
          actionTypes.PUT_REFERENCE,
          actionTypes.PUT_REFERENCE_SUCCESS,
          actionTypes.PUT_REFERENCE_FAIL,
        ],
        data,
        promise: (client) => client.put(
          `${API_HOST}/credit-application-mx/referencePerson`,
          referenceData
        ),
        afterSuccess: (dispatch) => dispatch(putScreenName(type)),
      };
    }

    case 'INCOME_VERIFICATION_WAY': {
      store.dispatch(putScreenName(type));
      if (loan.incomeVerificationWay === 'DOCUMENT_UPLOAD') {
        return postBankStatementAction(data, type);
      }
      return {
        type: actionTypes.SKIP_CZ_INCOME_VERIFICATION,
        data: { incomeVerificationWay: 'EMAIL' },
      };
    }

    case 'IDENTITY_VERIFICATION_WAY': {
      store.dispatch(putScreenName(type));
      if (loan.identityVerificationWay === 'DOCUMENT_UPLOAD') {
        return {
          types: [
            actionTypes.POST_IDENTITY_DOCUMENTS,
            actionTypes.POST_IDENTITY_DOCUMENTS_SUCCESS,
            actionTypes.POST_IDENTITY_DOCUMENTS_FAIL,
          ],
          promise: (client) => data.identityDocuments.reduce((chain, curr) => {
            const formData = new FormData();
            formData.append('file', curr);
            return chain.then(() => client.post(
              `${API_HOST}/file/upload-document`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            ));
          }, Promise.resolve()),
        };
      }
      return {
        type: actionTypes.SKIP_CZ_IDENTITY_VERIFICATION,
        data: { identityVerificationWay: 'EMAIL' },
      };
    }

    case 'BANK_STATEMENT': {
      const noFilesSelected = isEmpty(data.IBAN_PROOF)
        && isEmpty(data.INCOME_PROOF)
        && isEmpty(data.PASSPORT_A)
        && isEmpty(data.PASSPORT_B)
        && isEmpty(data.SELFIE);

      if (noFilesSelected) {
        return {
          type: actionTypes.SKIP_BANK_STATEMENTS,
        };
      }

      return postBankStatementAction(data, type);
    }

    case 'NORDIGEN_INFO':
      return {
        types: [
          actionTypes.PUT_NORDIGEN_CONSENT,
          actionTypes.PUT_NORDIGEN_CONSENT_SUCCESS,
          actionTypes.PUT_NORDIGEN_CONSENT_FAIL,
        ],
        promise: (client) => client.put(
          `${API_HOST}/customer/additional-consent`,
          [{
            consentType: 'NORDIGEN_CONSENT',
            name: 'NORDIGEN_CONSENT',
            value: data.NORDIGEN_CONSENT,
            revocable: false,
          }]
        ),
      };

    case STEP_REIDENTIFY_CUSTOMER.type: {
      const url = `${API_HOST}/customer/identification/msisdn-verification/finish`;

      return {
        types: [
          actionTypes.REIDENTIFY_CUSTOMER,
          actionTypes.REIDENTIFY_CUSTOMER_SUCCESS,
          actionTypes.REIDENTIFY_CUSTOMER_FAIL,
        ],
        data,
        promise: (client) => client.post(url, {
          msisdn: data.msisdn,
          otp: data.otp,
        }),
        afterSuccess: (dispatch, getState) => {
          const { authentication: { channel } } = getState();
          const channelData = getChannelDataForMarket(channel);
          dispatch(createCreditApplicationAfterReidentifyCustomer(channelData, data));
        },
      };
    }

    case 'CONSENTS_AU': {
      return {
        types: [
          actionTypes.PUT_MISSING_ADDITIONAL_CONSENT,
          actionTypes.PUT_MISSING_ADDITIONAL_CONSENT_SUCCESS,
          actionTypes.PUT_MISSING_ADDITIONAL_CONSENT_FAIL,
        ],
        promise: (client) => client.put(
          `${API_HOST}/customer/additional-consent`,
          REGISTRATION_CONSENTS_PAYLOAD
        ),
      };
    }

    default:
      return false;
  }
}

export function resetAddressSuggestions() {
  return {
    type: actionTypes.RESET_ADDRESS_SUGGESTIONS,
  };
}

export function resetRobinRedirectId() {
  return {
    type: actionTypes.RESET_ROBIN_REDIRECT_ID,
  };
}

export function skipCalma() {
  return {
    type: actionTypes.SKIP_CALMA,
  };
}

export function setCZIncomeVerificationWay(incomeVerificationWay) {
  return {
    type: actionTypes.SET_CZ_INCOME_VERIFICATION_WAY,
    data: {
      incomeVerificationWay,
    },
  };
}

export function setCZIdentityVerificationWay(identityVerificationWay) {
  return {
    type: actionTypes.SET_CZ_IDENTITY_VERIFICATION_WAY,
    data: {
      identityVerificationWay,
    },
  };
}

export function goToThankYouPage() {
  return {
    type: actionTypes.LOANSUBMITTED,
    meta: {
      analyticsLV: (storeState) => (
        getAnalyticsObject('thankYou', storeState)
      ),
    },
  };
}

export function goToBankStatementPage() {
  return {
    type: actionTypes.GO_TO_BANK_STATEMENT,
  };
}

export function skipBankStatement() {
  return {
    type: actionTypes.SKIP_BANK_STATEMENTS,
  };
}

export function goBackToIncomePage() {
  return {
    type: actionTypes.BACK_TO_INCOME,
  };
}

export function goBackToVerificationPage() {
  return {
    type: actionTypes.BACK_TO_VERIFICATION,
  };
}

export function loanFormSetStep(index) {
  return {
    type: actionTypes.SET_STEP,
    data: index,
  };
}

export function loanFormStepBack() {
  return {
    type: actionTypes.STEP_BACK,
  };
}

export function goToCreditLimit() {
  return {
    type: actionTypes.GO_TO_CREDIT_LIMIT,
  };
}

export function getIlProducts() {
  const URL = `${API_HOST}/products/INSTALLMENT`;
  return {
    types: [
      actionTypes.GET_IL_PRODUCTS,
      actionTypes.GET_IL_PRODUCTS_SUCCESS,
      actionTypes.GET_IL_PRODUCTS_FAIL,
    ],
    promise: (client) => client.get(`${URL}`, { hideError: true }),
  };
}

export function getAvailableInstallmentProducts(silent = false, creditApplicationId = null) {
  let URL = `${API_HOST}/credit-application/product/availability/INSTALLMENT`;
  if (creditApplicationId) {
    URL = `${API_HOST}/hybrid/application/${creditApplicationId}/products`;
  }
  return {
    types: [
      actionTypes.GET_INSTALLMENT,
      actionTypes.GET_INSTALLMENT_SUCCESS,
      actionTypes.GET_INSTALLMENT_FAIL,
    ],
    promise: (client) => client.get(URL, { silent }),
    afterSuccess: (dispatch, getState) => {
      if (COUNTRY === 'ES') {
        dispatch(change('loan', 'selectedMaturity', '36'));
      }
      if (COUNTRY === 'PL' || COUNTRY === 'LT') {
        const { loan } = getState();
        dispatch(
          change('loan', 'selectedMaturity', loan.selectedProduct.maturityPeriods)
        );
      }
    },
    meta: {
      analyticsPL: (storeState, data) => {
        if (storeState.loan.isFilledByBroker) {
          return getAnalyticsObject('7', storeState, data);
        }
        return null;
      },
    },
  };
}

export function getAvailableProducts() {
  const URL = `${API_HOST}/credit-application/product/availability/CREDIT_LINE`;
  return {
    types: [
      actionTypes.GET_PRODUCTS,
      actionTypes.GET_PRODUCTS_SUCCESS,
      actionTypes.GET_PRODUCTS_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
    afterSuccess: (dispatch, getState, response) => {
      const { loan } = getState();
      if (loan.selectedProduct && loan.selectedProduct.id) {
        let periods;
        if (COUNTRY === 'MX' || MOBILE_WALLET) {
          periods = [loan.selectedProduct.maturityPeriods];
        } else if (COUNTRY === 'CZ' || COUNTRY === 'ES') {
          periods = loan.selectedProduct.maturitiesToLoad;
        } else if (!PRODUCT_IL && loan.productType === 'CREDIT_LINE') {
          periods = possiblePeriodsCL;
        }
        if (COUNTRY === 'LT') {
          dispatch(change('loan', 'creditLimit', `${fromCents(loan.selectedProduct.principal)}`));
        } else if (COUNTRY !== 'EE' && !(COUNTRY === 'LV' && VARIANT_B)) {
          if (periods) {
            if (COUNTRY !== 'ES') {
              const preSelectedMaturity = loan.product.maturityPeriods;
              if (preSelectedMaturity && !periods.includes(preSelectedMaturity)) {
                periods = [...periods, preSelectedMaturity];
              }
            }
            dispatch(getMMPsByMonth(periods, loan.selectedProduct.id));
          }
        }
      }
      if (COUNTRY === 'ES') {
        const currentStepType = loan.steps[loan.currentStep].type;
        if (currentStepType === 'PRODUCT' && !loan.fees) {
          dispatch(getFees(loan.selectedProduct.id));
        }
        if (response.data.length === 0) {
          dispatch(getAvailableInstallmentProducts());
        }
        const { creditApplicationId } = loan;
        if (creditApplicationId) {
          dispatch(closeGuarantorApplication(creditApplicationId));
        }
      }
    },
    afterFailure: (dispatch, getState) => {
      affiliateRequestCustomerRejected(dispatch, getState);
    },
  };
}

export function getAllProducts() {
  const URL = `${API_HOST}/credit-application/product/availability/CREDIT_LINE`;
  return {
    types: [
      actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT,
      actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT_SUCCESS,
      actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
  };
}

/**
 * Action for homepage slider. First request to drawSelectionsUrl endpoint
 * is to identify country specific groupName configured on a server side.
 * Second request is to get drawSelections filtered by groupName received from first request.
 * @param {number} maturityPeriod
 * @param {string} creditType
 */
export function getMMPsByGroup(maturityPeriod, creditType) {
  const drawSelectionsUrl = `${API_HOST}/products/draw-selections-with-maturity-product-group`;
  const productsUrl = `${API_HOST}/products/${creditType}`;
  return {
    types: [
      actionTypes.GET_MMPS_BY_GROUP,
      actionTypes.GET_MMPS_BY_GROUP_SUCCESS,
      actionTypes.GET_MMPS_BY_GROUP_FAIL,
    ],
    data: { maturityPeriod },
    promise: (client) => (
      client.get(productsUrl, { hideError: true })
        .then((value) => {
          const { groupName } = value.data[0];
          return client.get(`${drawSelectionsUrl}`, {
            params: {
              maturityPeriod,
              groupName,
            },
            hideError: true,
          });
        })
    ),
  };
}

export function setLoanPrincipal(principal) {
  savePrincipalValue(toCents(principal));
  return {
    type: actionTypes.SET_PRINCIPAL,
    data: principal,
  };
}

export function setLoanMaturityPeriods(maturityPeriods) {
  return {
    type: actionTypes.SET_MATURITY_PERIODS,
    data: maturityPeriods,
  };
}

export function getPostOffices() {
  const URL = `${API_HOST}/authentication/ee/postoffices`;
  return {
    types: [
      actionTypes.GET_POST_OFFICES,
      actionTypes.GET_POST_OFFICES_SUCCESS,
      actionTypes.GET_POST_OFFICES_FAIL,
    ],
    promise: (client) => client.get(URL),
  };
}

export function getCitizenships() {
  return {
    types: [
      actionTypes.GET_CITIZENSHIPS,
      actionTypes.GET_CITIZENSHIPS_SUCCESS,
      actionTypes.GET_CITIZENSHIPS_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/citizenships`),
  };
}

export function getLoanIssuers() {
  return {
    types: [
      actionTypes.GET_LOAN_ISSUERS,
      actionTypes.GET_LOAN_ISSUERS_SUCCESS,
      actionTypes.GET_LOAN_ISSUERS_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application/loanissuers`),
  };
}

export const getOfferProductsAU = (productType) => {
  const isCreditLine = productType === 'CREDIT_LINE';
  let offerType;
  if (INCREASE_APP) {
    offerType = isCreditLine ? 'upgrade' : 'upsell';
  } else {
    offerType = isCreditLine ? 'credit-line' : 'installment';
  }
  const URL = `${API_HOST}/credit-application-au/product-offer/${offerType}`;

  return {
    types: [
      actionTypes.GET_OFFER_PRODUCTS_AU,
      actionTypes.GET_OFFER_PRODUCTS_AU_SUCCESS,
      actionTypes.GET_OFFER_PRODUCTS_AU_FAIL,
    ],
    data: { productType },
    promise: (client) => client.get(URL, { hideError: true }),
    afterSuccess: (dispatch, getState, response) => {
      if (!INCREASE_APP) {
        if (isCreditLine && response.data.products.length === 0) {
          dispatch(getOfferProductsAU('INSTALLMENT'));
        }
      } else {
        const highestCreditAmount = Math.max(...response.data.products.map((p) => p.creditAmount));
        dispatch(change('loan', 'creditLimit', `${highestCreditAmount}`));
      }
    },
  };
};

export function getApplicableCampaigns() {
  const URL = `${API_HOST}/campaigns/applicable`;
  return {
    types: [
      actionTypes.GET_APPLICABLE_CAMPAIGNS,
      actionTypes.GET_APPLICABLE_CAMPAIGNS_SUCCESS,
      actionTypes.GET_APPLICABLE_CAMPAIGNS_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
  };
}

export function getCountryList() {
  let URL = `${API_HOST}/customer/countrylist`;
  if (COUNTRY === 'ES') {
    URL = `${API_HOST}/customer-es/countrylist`;
  }
  if (COUNTRY === 'CZ') {
    URL = `${API_HOST}/customer-cz/countrylist`;
  }
  if (COUNTRY === 'LT') {
    URL = `${API_HOST}/customer-lt/countrylist`;
  }
  return {
    types: [
      actionTypes.GET_COUNTRY_LIST,
      actionTypes.GET_COUNTRY_LIST_SUCCESS,
      actionTypes.GET_COUNTRY_LIST_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
  };
}

export function getAddressSuggestionsCZ(value) {
  return {
    types: [
      actionTypes.GET_ADDRESS_SUGGESTIONS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_SUCCESS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_FAIL,
    ],
    data: { type: 'address' },
    promise: (client) => client.post(
      `${API_HOST}/customer-cz/ruian-address-suggestion`,
      { userInput: value }
    ),
  };
}

export function getAddressSuggestions(name, autofill = false) {
  let type = name;
  if (autofill) {
    switch (name) {
      case 'postcode1':
        type = 'city1';
        break;
      case 'city1':
        type = 'street1';
        break;
      case 'postcode2':
        type = 'city2';
        break;
      case 'city2':
        type = 'street2';
        break;
      default:
    }
  }
  const {
    postcode1,
    city1,
    street1,
    postcode2,
    city2,
    street2,
  } = store.getState().form.loan.values;

  let params = {};
  if (type === 'postcode1') {
    params = {
      postcode: postcode1,
    };
  }
  if (type === 'city1') {
    params = {
      postcode: postcode1,
      city: city1,
    };
  }
  if (type === 'street1') {
    params = {
      postcode: postcode1,
      city: city1,
      street: street1,
    };
  }
  if (type === 'postcode2') {
    params = {
      postcode: postcode2,
    };
  }
  if (type === 'city2') {
    params = {
      postcode: postcode2,
      city: city2,
    };
  }
  if (type === 'street2') {
    params = {
      postcode: postcode2,
      city: city2,
      street: street2,
    };
  }
  if (autofill) {
    if (type === 'city1' || type === 'city2') {
      params.city = '%';
    }
    if (type === 'street1' || type === 'street2') {
      params.street = '%';
    }
  }
  const URL = `${API_HOST}/customer/addresses`;
  return {
    types: [
      actionTypes.GET_ADDRESS_SUGGESTIONS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_SUCCESS,
      actionTypes.GET_ADDRESS_SUGGESTIONS_FAIL,
    ],
    data: { type, autofill },
    promise: (client) => client.get(URL, {
      params,
      hideError: true,
    }),
    afterSuccess: (dispatch, getState, response) => {
      if (autofill) {
        if (response.data.length === 1) {
          dispatch(
            change('loan', type, response.data[0])
          );
          if (type !== 'street1' || type !== 'street2') {
            let newType;
            switch (type) {
              case 'postcode1':
                newType = 'city1';
                break;
              case 'city1':
                newType = 'street1';
                break;
              case 'postcode2':
                newType = 'city2';
                break;
              case 'city2':
                newType = 'street2';
                break;
              default:
            }
            dispatch(
              getAddressSuggestions(newType, true)
            );
          }
        }
      }
    },
  };
}

export function setMXAddress(data, requestedPostalCode) {
  const { postalCode } = store.getState().form.loan.values;
  return {
    type: actionTypes.SET_MX_ADDRESS,
    data: postalCode === requestedPostalCode ? data : [],
  };
}

export function getMXAddress(postalCode) {
  const URL = `${API_HOST}/customer-mx/address-autofill?postcode=${postalCode}`;
  return {
    types: [
      actionTypes.GET_MX_ADDRESS,
      actionTypes.GET_MX_ADDRESS_SUCCESS,
      actionTypes.GET_MX_ADDRESS_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
  };
}

export function showBankName(bankName) {
  return {
    type: actionTypes.BANK_NAME,
    data: bankName,
  };
}

export function setInitialLoanDataEE(data) {
  const cookiesManager = getCookiesManager();
  if (cookiesManager) {
    cookiesManager.saveValue(data.principal, 'loanPrincipal');
    cookiesManager.saveValue(data.maturityPeriods, 'loanTerm');
    cookiesManager.saveValue(data.personalDataConsent ? 1 : 0, 'personalDataConsent');
  }
  return {
    type: actionTypes.SET_INITIAL_LOAN_DATA_EE,
    data,
  };
}

export function postEIDAuthorizationToken() {
  return {
    types: [
      actionTypes.POST_EID_AUTHORIZATION_TOKEN,
      actionTypes.POST_EID_AUTHORIZATION_TOKEN_SUCCESS,
      actionTypes.POST_EID_AUTHORIZATION_TOKEN_FAIL,
    ],
    promise: (client) => client.post(`${API_HOST}/eid/init-session`),
  };
}

export function saveEIDResult(data, finishedDueToHarshError) {
  const URL = `${API_HOST}/eid/finish${VERIFICATION_APP ? '-adhoc' : ''}-session`;
  return {
    types: [
      actionTypes.SAVE_EID_RESULT,
      actionTypes.SAVE_EID_RESULT_SUCCESS,
      actionTypes.SAVE_EID_RESULT_FAIL,
    ],
    promise: (client) => client.post(URL, data, { finishedDueToHarshError }),
  };
}

export function putKontomatikSession(id, signature) {
  return {
    types: [
      actionTypes.PUT_KONTOMATIK_SESSION,
      actionTypes.PUT_KONTOMATIK_SESSION_SUCCESS,
      actionTypes.PUT_KONTOMATIK_SESSION_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application/kontomatik-session`,
      { id, signature },
      { hideError: true }
    ),
  };
}

export function toggleAddressDetails() {
  return {
    type: actionTypes.TOGGLE_ADDRESS_DETAILS,
  };
}

export function getLegalDocument() {
  return {
    types: [
      actionTypes.GET_LEGAL_DOCUMENT,
      actionTypes.GET_LEGAL_DOCUMENT_SUCCESS,
      actionTypes.GET_LEGAL_DOCUMENT_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application-au/legal-page`),
  };
}

export function postCustomerNotificationPL(customerId, notificationType) {
  return {
    types: [
      actionTypes.POST_CUSTOMER_PL_NOTIFICATION,
      actionTypes.POST_CUSTOMER_PL_NOTIFICATION_SUCCESS,
      actionTypes.POST_CUSTOMER_PL_NOTIFICATION_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/customer-pl/${customerId}/notifications`,
      { notificationType },
      { hideError: true, preventLogout: true }
    ),
  };
}

export function handleSubmitLoanStepError(type, errors, loan) {
  if (type === 'ADDRESS_AU' && loan.areAddressDetailsHidden) {
    return toggleAddressDetails();
  }
  if (COUNTRY === 'AU' && type === 'LOAN_PURPOSE') {
    window.scroll(0, 0);
  }
  return null;
}

export function getLibertoToken() {
  const { location } = window;
  return {
    types: [
      actionTypes.POST_LIBERTO_TOKEN,
      actionTypes.POST_LIBERTO_TOKEN_SUCCESS,
      actionTypes.POST_LIBERTO_TOKEN_FAIL,
    ],
    promise: (client) => (
      client.post(
        `${API_HOST}/customer-es/capture-token-ca`,
        { callbackUrl: `${location.protocol}//${location.host}` }
      )
    ),
  };
}

export function saveLibertoSuccess() {
  return {
    type: actionTypes.SAVE_LIBERTO_SUCCESS,
  };
}

export function getMXBanks() {
  return {
    types: [
      actionTypes.GET_MX_BANKS,
      actionTypes.GET_MX_BANKS_SUCCESS,
      actionTypes.GET_MX_BANKS_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/customer-mx/banks`),
  };
}

export function initOtpEs(data, loan) {
  const { steps, currentStep } = loan;
  const isCalmaStep = steps[currentStep].type === 'CALMA';
  const url = isCalmaStep
    ? `${API_HOST}/credit-application-es/calma/request-pin`
    : `${API_HOST}/credit-application-es/submit-init-pin-es`;
  const payload = isCalmaStep
    ? {}
    : {
      ssn: data.ssn,
      msisdn: normalizeMsisdn(data.msisdn),
    };
  return {
    types: [
      actionTypes.INIT_OTP,
      actionTypes.INIT_OTP_SUCCESS,
      actionTypes.INIT_OTP_FAIL,
    ],
    promise: (client) => client.post(
      url,
      payload,
      { hideError: true }
    ),
  };
}

export function showOtpSection() {
  return {
    type: actionTypes.SHOW_OTP_SECTION,
  };
}

export function hideOtpSection() {
  return {
    type: actionTypes.HIDE_OTP_SECTION,
  };
}

export function getChannelList() {
  return {
    types: [
      actionTypes.GET_CHANNEL_LIST,
      actionTypes.GET_CHANNEL_LIST_SUCCESS,
      actionTypes.GET_CHANNEL_LIST_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application/channellist`,
      { hideError: true }
    ),
  };
}

export function getBankAccount() {
  return {
    types: [
      actionTypes.GET_BANK_ACCOUNT,
      actionTypes.GET_BANK_ACCOUNT_SUCCESS,
      actionTypes.GET_BANK_ACCOUNT_FAIL,
    ],
    promise: (client) => client.get(`${API_HOST}/credit-application-au`),
  };
}

export function getFinpassBanks() {
  return {
    types: [
      actionTypes.GET_FINPASS_BANKS,
      actionTypes.GET_FINPASS_BANKS_SUCCESS,
      actionTypes.GET_FINPASS_BANKS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/customer-lt/finpass-token`
    ).then(
      (tokenResponse) => axios.create()({
        url: 'https://psd2api.ondato.com/api/getbanks',
        method: 'post',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${tokenResponse.data.finpassOAuthToken}`,
          'Content-Type': 'application/json',
        },
      }).then((banksResponse) => Promise.resolve({
        token: tokenResponse.data.finpassOAuthToken,
        banks: banksResponse.data.data,
      }))
    ),
  };
}

export function getFinpassRedirectUrl(token, data) {
  return {
    types: [
      actionTypes.GET_FINPASS_REDIRECT_URL,
      actionTypes.GET_FINPASS_REDIRECT_URL_SUCCESS,
      actionTypes.GET_FINPASS_REDIRECT_URL_FAIL,
    ],
    promise: () => axios.create()({
      data,
      url: 'https://psd2api.ondato.com/api/startrequest',
      method: 'post',
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }),
  };
}

export function startFinpass(msgId, redirectUrl) {
  return {
    types: [
      actionTypes.START_FINPASS,
      actionTypes.START_FINPASS_SUCCESS,
      actionTypes.START_FINPASS_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application-lt/finpass-msgid`,
      { msgId }
    ),
    afterSuccess: (dispatch) => {
      dispatch(
        putMarketingConsents(
          { marketingConsents: [{ consentName: 'FINPASS_LT', version: 'FINPASS_LT' }] },
          { FINPASS_LT: true },
          () => {
            window.location.assign(redirectUrl);
          }
        )
      );
    },
  };
}

export function finishFinpassJourney() {
  return {
    type: actionTypes.FINISH_FINPASS,
  };
}

export function putReferer(partnerID, transactionId) {
  return {
    types: [
      actionTypes.PUT_REFERER,
      actionTypes.PUT_REFERER_SUCCESS,
      actionTypes.PUT_REFERER_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application/referer`,
      { partnerID, transactionId },
      { hideError: true }
    ),
  };
}

export function getAutoApprovalStatus() {
  return {
    types: [
      actionTypes.GET_AUTO_APPROVAL_STATUS,
      actionTypes.GET_AUTO_APPROVAL_STATUS_SUCCESS,
      actionTypes.GET_AUTO_APPROVAL_STATUS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/credit-application-ee/auto-approval-status`,
      { hideError: true }
    ),
  };
}

export function putNordigenRequestId(requestId) {
  return {
    types: [
      actionTypes.PUT_NORDIGEN_REQUEST_ID,
      actionTypes.PUT_NORDIGEN_REQUEST_ID_SUCCESS,
      actionTypes.PUT_NORDIGEN_REQUEST_ID_FAIL,
    ],
    promise: (client) => client.put(
      `${API_HOST}/credit-application/nordigen/request-id`,
      { requestId }
    ),
  };
}

export function eidShowPopup() {
  return {
    type: actionTypes.EID_SHOW_POPUP,
  };
}

export function closePopup() {
  return {
    type: actionTypes.CLOSE_POPUP,
  };
}

export function libertoFail(data) {
  return {
    type: actionTypes.LIBERTO_FAIL,
    data,
  };
}

export function setGuarantorType(guarantorType) {
  return {
    type: actionTypes.SET_GUARANTOR_TYPE,
    data: guarantorType,
  };
}

export const skipEid = () => ({ type: actionTypes.SKIP_EID });

export const skipKontomatik = () => ({ type: actionTypes.SKIP_KONTOMATIK });

export function setRejection(errorData) {
  const { message } = errorData.data;
  return {
    type: actionTypes.SET_REJECTION,
    data: {
      statusCode: 'default',
      message,
      params: {},
    },
  };
}

export function toggleConfirmationButtonVisibility(data) {
  return {
    type: actionTypes.TOGGLE_CONFIRMATION_BUTTON_VISIBILITY,
    data,
  };
}

export function onfidoAuthenticationSuccess(token, applicantId) {
  return {
    type: actionTypes.POST_ONFIDO_AUTHORIZATION_SUCCESS,
    payload: { token, applicantId },
  };
}

export function onfidoAuthenticationFailure(errorMessage) {
  return {
    type: actionTypes.POST_ONFIDO_AUTHORIZATION_FAIL,
    payload: { errorMessage },
  };
}

export function onfidoCheckSuccess() {
  return {
    type: actionTypes.POST_ONFIDO_CHECK_SUCCESS,
  };
}

export function onfidoCheckSkipped() {
  return {
    type: actionTypes.POST_ONFIDO_SKIPPED,
  };
}

export function onfidoCheckFailure(errorMessage) {
  return {
    type: actionTypes.POST_ONFIDO_CHECK_FAIL,
    payload: { errorMessage },
  };
}

export function onfidoCheck(applicantId) {
  return {
    types: [
      actionTypes.POST_ONFIDO_CHECK,
      actionTypes.POST_ONFIDO_CHECK_SUCCESS,
      actionTypes.POST_ONFIDO_CHECK_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/onfido/check`,
      {
        applicantId,
        countryCode: COUNTRY,
      }
    ),
    afterSuccess: (dispatch) => {
      dispatch(onfidoCheckSuccess());
    },
    afterFailure: (dispatch, getState, response) => {
      dispatch(onfidoCheckFailure(response.data.message));
    },
  };
}

export function getContract() {
  const { authentication: { contracts } } = store.getState();

  if (contracts.length === 0) {
    return {
      type: actionTypes.GET_CONTRACT_FAIL,
    };
  }
  const contractId = contracts[0];
  const url = `${API_HOST}/contracts/${contractId}`;

  return {
    types: [
      actionTypes.GET_CONTRACT,
      actionTypes.GET_CONTRACT_SUCCESS,
      actionTypes.GET_CONTRACT_FAIL,
    ],
    promise: (client) => client.get(url, { hideError: true }),
  };
}

export function getMigrationMmps(productIds) {
  const productList = productIds.join(',');
  const url = `${API_HOST}/credit-application-es/migration-draw-selections?productIdList=${productList}`;
  return {
    types: [
      actionTypes.GET_MIGRATION_MMPS,
      actionTypes.GET_MIGRATION_MMPS_SUCCESS,
      actionTypes.GET_MIGRATION_MMPS_FAIL,
    ],
    promise: (client) => client.get(url, { hideError: true }),
  };
}

export function getAvailableMigrationProducts() {
  const URL = `${API_HOST}/credit-application/product/availability/migration/CREDIT_LINE`;
  return {
    types: [
      actionTypes.GET_MIGRATION_PRODUCTS,
      actionTypes.GET_MIGRATION_PRODUCTS_SUCCESS,
      actionTypes.GET_MIGRATION_PRODUCTS_FAIL,
    ],
    promise: (client) => client.get(URL, { hideError: true }),
    afterSuccess: (dispatch, getState, response) => {
      const productIds = response.data.map((element) => element.id);
      dispatch(getMigrationMmps(productIds));
      dispatch(getContract());
    },
    afterFailure: (dispatch, getState) => {
      affiliateRequestCustomerRejected(dispatch, getState);
    },
  };
}

// eslint-disable-next-line max-len
export const getMigrationApplicationScoring = (scoringId) => ({
  types: [
    actionTypes.GET_MIGRATION_SCORING_ES,
    actionTypes.GET_MIGRATION_SCORING_ES_SUCCESS,
    actionTypes.GET_MIGRATION_SCORING_ES_FAIL,
  ],
  promise: (client) => client.get(
    `${API_HOST}/credit-application-es/scoring/${scoringId}`
  ),
  afterSuccess: (dispatch, getState, response) => {
    const { loan, authentication: { isSelfServiceImpersonation } } = getState();

    if (loan.isWaitingForOffer && !loan.finalState) {
      window.setTimeout(
        () => {
          dispatch(getMigrationApplicationScoring(scoringId));
        },
        loan.waitingForOfferInterval
      );
    }

    if (response.data.currentStep === 'REJECTED') {
      dispatch(closeGuarantorApplication(loan.id));
    } else if (response.data.currentStep === 'FINISHED' && isSelfServiceImpersonation) {
      dispatch(getAvailableMigrationProducts());
    }
  },
});

// eslint-disable-next-line max-len
export function postMigrationApplicationScoring() {
  return {
    types: [
      actionTypes.POST_MIGRATION_SCORING_ES,
      actionTypes.POST_MIGRATION_SCORING_ES_SUCCESS,
      actionTypes.POST_MIGRATION_SCORING_ES_FAIL,
    ],
    promise: (client) => client.post(
      `${API_HOST}/credit-application-es/migration-scoring`,
      { channel: 'migration' }
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { loan } = getState();

      if (loan.isWaitingForOffer && !loan.finalState) {
        window.setTimeout(
          () => {
            dispatch(getMigrationApplicationScoring(response.data.id));
          },
          loan.waitingForOfferInterval
        );
      }
      if (response.data.currentStep === 'REJECTED') {
        dispatch(closeGuarantorApplication(loan.id));
      }
    },
  };
}

export function makeMigrationApplicationScoring() {
  const { loan } = store.getState();
  const { scoringId } = loan;

  if (scoringId) {
    return getMigrationApplicationScoring(scoringId);
  }

  return postMigrationApplicationScoring();
}

export function setLoanSubmitting() {
  return {
    type: actionTypes.SET_LOAN_SUBMITTING,
  };
}

export function unsetLoanSubmitting() {
  return {
    type: actionTypes.UNSET_LOAN_SUBMITTING,
  };
}

export function getNordigenBanks(country) {
  return {
    types: [
      actionTypes.GET_NORDIGEN_BANKS,
      actionTypes.GET_NORDIGEN_BANKS_SUCCESS,
      actionTypes.GET_NORDIGEN_BANKS_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/nordigen/banks/${country}`
    ),
    afterFailure: (dispatch) => {
      const standalonePages = ['apply-nordigen', 'nordigen-error', 'nordigen-expired'];
      const isStandalonestep = standalonePages.some((el) => window.location.href.includes(el));
      if (isStandalonestep) {
        window.location.href = `${BASE_PATH}nordigen-error`;
      } else {
        dispatch(setNordigenError(true));
        dispatch(skipNordigenStep());
      }
    },
  };
}

export function setNordigenBank(bank) {
  return {
    type: actionTypes.SET_NORDIGEN_BANK,
    data: bank,
  };
}

export function getAccountFromNordigen(id) {
  return {
    types: [
      actionTypes.GET_NORDIGEN_ACCOUNT,
      actionTypes.GET_NORDIGEN_ACCOUNT_SUCCESS,
      actionTypes.GET_NORDIGEN_ACCOUNT_FAIL,
    ],
    promise: (client) => client.get(
      `${API_HOST}/nordigen/details/${id}`
    ),
    afterSuccess: (dispatch, getState, response) => {
      const accountList = response.data.filter((el) => (el.status === 'READY' && el));

      if (accountList.length > 0) {
        dispatch(setNordigenError(false));
      } else {
        dispatch(setNordigenError(true));
      }

      if (accountList.length === 1) {
        dispatch(change('loan', 'bankAccount', accountList[0].iban));
      }
    },
    afterFailure: (dispatch) => {
      dispatch(setNordigenError(true));
    },
  };
}

export function setRequestedFinancialData(data) {
  return {
    type: actionTypes.SET_REQUESTED_FINANCIAL_DATA,
    data,
  };
}

export function removeIdentityVerificationWay() {
  return {
    type: actionTypes.REMOVE_IDENTIFY_VERIFICATION_WAY,
  };
}

export function touchFormFields(fields) {
  return {
    type: '@@redux-form/TOUCH',
    meta: {
      form: 'loan',
      fields,
    },
  };
}

export function getFollowUpQuestions() {
  const url = `${API_HOST}/credit-application/questions`;

  return {
    types: [
      actionTypes.GET_FOLLOWUP_QUESTIONS,
      actionTypes.GET_FOLLOWUP_QUESTIONS_SUCCESS,
      actionTypes.GET_FOLLOWUP_QUESTIONS_FAIL,
    ],
    promise: (client) => client.get(url),
    afterFailure: () => {
      sessionRepository.saveItem('followUpError', true);
    },
  };
}

export function putFollowUpQuestions(answers) {
  const url = `${API_HOST}/credit-application/questions/answers`;

  return {
    types: [
      actionTypes.PUT_FOLLOWUP_QUESTIONS,
      actionTypes.PUT_FOLLOWUP_QUESTIONS_SUCCESS,
      actionTypes.PUT_FOLLOWUP_QUESTIONS_FAIL,
    ],
    promise: (client) => (
      client.put(url, { answers })
    ),
  };
}

export const getCenterlinkReportsStatus = (firstRequestStartDate, currentRequestStartDate) => {
  const url = `${API_HOST}/credit-application-au/bank-scraping-reports/CENTRELINK/status`;

  const maxRequestTime = 180000; // 3 min
  const latency = 2000;

  return {
    types: [
      actionTypes.GET_CENTERLINK_REPORTS_STATUS,
      actionTypes.GET_CENTERLINK_REPORTS_STATUS_SUCCESS,
      actionTypes.GET_CENTERLINK_REPORTS_STATUS_FAIL,
    ],
    data: { firstRequestStartDate, maxRequestTime },
    promise: (client) => client.get(
      url,
      {
        hideError: true,
        metadata: { startTime: currentRequestStartDate },
        timeout: maxRequestTime,
      }
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { status } = response.data;
      const currentDate = new Date();
      const requestTime = currentDate - currentRequestStartDate;
      const firstRequestTime = currentDate - firstRequestStartDate;

      if (status === CENTERLINK_STATUS.READY) {
        dispatch(putScreenName(STEP_SUBMIT.type, true));
      } else if (firstRequestTime < maxRequestTime) {
        setTimeout(() => {
          dispatch(getCenterlinkReportsStatus(firstRequestStartDate, new Date()));
        }, latency - requestTime);
      } else {
        dispatch(putScreenName(STEP_INFORMATION.type, true));
      }
    },
    afterFailure: (dispatch) => {
      dispatch(putScreenName(STEP_INFORMATION.type, true));
    },
  };
};

export function getRejectedApplicationLink(userData) {
  const url = `${API_HOST}/customer-au/lead-market`;

  return {
    types: [
      actionTypes.GET_REJECTED_APPLICATION_TOKEN,
      actionTypes.GET_REJECTED_APPLICATION_TOKEN_SUCCESS,
      actionTypes.GET_REJECTED_APPLICATION_TOKEN_FAIL,
    ],
    promise: (client) => (
      client.post(url, { ...userData }, { hideError: true })
    ),
    afterSuccess: (dispatch, getState, response) => {
      const { token } = response.data;
      dispatch(logout());
      window.location = `${getRootURL()}lm-out?token=${token}`;
    },
    afterFailure: (dispatch) => {
      dispatch(logout());
      window.location = `${getRootURL()}lm-out`;
    },
  };
}
